import {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react';
import { ArrowsInIcon, ArrowsOutIcon, PlusIcon } from '@phosphor-icons/react';
import { T, Var, useGT, useMessages } from 'gt-react';
import { HotTable, HotTableRef } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import Handsontable from 'handsontable';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TablePagination } from 'src/components/common/TablePagination';
import { PaginationConfig } from 'src/components/common/PaginatedGentlyTable';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import { useToast } from '@/context/ToastContext';
import { useReviewFocus, type ReviewFocus } from './reviewFocus';
import {
  locateAs400ExportRow,
  useAs400ExportView,
  useResolveItemNumbers,
} from '@/api/rpc/hooks/inventory-management/useItemCatalog';
import { useChangeSetCommentThreads } from '@/api/rpc/hooks/inventory-management/useChangeSetComments';
import {
  useAs400ItemAddIndex,
  useAs400ItemSheetIndex,
} from './useAs400ItemAddIndex';
import {
  useAddChangeSetNewRow,
  useDeleteChangeSetNewRow,
  useStageDataLinkEdits,
  useUpdateChangeSetNewRows,
} from '@/api/rpc/hooks/inventory-management/useMetadataChangeSets';
import {
  AS400_CDB_COLUMN,
  AS400_EXPORT_GROUP_SEARCH_COLUMN,
  AS400_EXPORT_WORKSHEET_COLUMNS,
  as400ColumnEditKey,
  as400ColumnNumberFormats,
  as400DivisionCode,
  as400DivisionExportsPerBranch,
  compareAs400RowOrder,
} from '@erp/rpc';
import { as400ReviewGridColumnClassName } from './as400ReviewGridDisplay';
import {
  paintSupplierBandHeader,
  paintSupplierCell,
  paintSupplierHeader,
  supplierBandRow,
  supplierNumbersOf,
  syncSupplierBandInset,
} from './as400SupplierGroups';
import type { ColumnWidthsByKey } from './useColumnWidths';
import type {
  As400ExportColumn,
  As400ExportGroup,
  As400ExportRow,
  As400RowOrderKey,
  CatalogAdvancedFilter,
  ChangeSetCommentThread,
  ChangeSetNewRow,
} from '@erp/rpc';
import { AddNewRowButton, HiddenNewRowsNote } from './NewRowControls';
import { As400GridSortChip } from './As400GridSortChip';
import {
  nextSortFor,
  sortedColumn,
  sortNewRows,
  type GridSorting,
  type SortedColumn,
} from './as400GridSort';
import {
  renderSortToggle,
  SORT_TOGGLE_HOST_CLASSES,
  sortToggleColumn,
  sortToggleFrom,
  type SortToggleState,
} from './as400GridSortToggle';
import { As400GridHidingControls } from './As400GridHidingControls';
import {
  hasHiddenRows,
  hiddenRowIndexes,
  NO_HIDDEN_ROWS,
  rowHidingActions,
  type GridRowHiding,
  type RowHidingActions,
} from './as400HiddenRows';
import type { ReviewGridLayout } from './reviewViewSession';
import {
  AS400_EXPORT_GROUP_LABELS,
  AS400_EXPORT_GROUP_ORDER,
} from './as400Groups';
import { applyGridHiding, enableGridHiding } from './as400GridHiding';
import {
  expandEditToSelection,
  selectedCells,
  selectRecordCell,
  writableCellAt,
  type BlankMarkerFor,
} from './as400GridSelection';
import {
  blankMarkerLookup,
  blankOutOnDelete,
  blankOutSelection,
  cellBlankOuts,
} from './as400BlankOut';
import {
  RESET_SOURCE,
  resetToAs400Item,
  resetToSavedItem,
} from './as400GridReset';
import {
  newRowLocationSeed,
  newRowValuesAt,
  parseNewRowCdb,
  type NewRowTarget,
} from './newRowTargets';
import {
  useDivisionOptions,
  useDivisionSelection,
  type DivisionOption,
} from './useDivisionSelection';
import { asCellText, buildAs400PaginationConfig } from './as400ExportParts';
import {
  applyCellEdit,
  buildGridRecords,
  correctSlashCountCellChanges,
  isGridRecord,
  rewriteGridCellChanges,
  gridRecordsNeedRebuild,
  type NonAsciiCellRejection,
  type PendingEditsByRowKey,
  type PendingRowEdit,
} from './as400ExportEditState';
import {
  as400AddRowDefaults,
  as400CellAutoFills,
  as400CellRules,
  isBlocking,
  type CellRowLookups,
  type CellRulesByField,
} from '@erp/item-master-rules';
import type { CellAutoFill, CellViolation } from '@erp/item-master-rules';
import { pendingViolations } from './as400GridCellRules';
import { autoFillGridChanges, withAddRowDefaults } from './as400CellAutoFill';
import {
  itemNumberLookupBlocksSave,
  pendingAddItemNumbers,
  takenItemNumbersOf,
} from './sheetRules/itemNumberTruncation';
import {
  draftRowKey,
  isDraftRowKey,
  isNewRowKey,
  newRowIdOf,
  newRowKey,
} from '@erp/item-master-rules';
import {
  columnNamer,
  describeCellViolation,
  itemNumberLookupNotice,
  leftCellNotice,
  saveBlockedNotice,
} from './as400CellViolationMessages';
import type { EditedRow, ErrorScope } from './as400ReviewErrors';
import { useAs400CellRowLookups } from './useAs400CellRowLookups';
import {
  useReviewErrorNavigation,
  type ReviewErrorNavigation,
} from './useReviewErrorNavigation';
import { ReviewErrorsNavigator } from './ReviewErrorsNavigator';
import { useWorksheetCellJump } from './useWorksheetCellJump';
import { createValueStore, type ValueStore } from './valueStore';
import {
  cellCommentMarker,
  commentedCellFrom,
  commentPreviewText,
  followedCellRect,
  indexThreadsByCell,
  isCommentableCell,
  isOnCommentMarker,
  NO_CELL_THREADS,
  paintCommentMarker,
  threadsForCell,
  type CellThreadIndex,
} from './comments/cellComments';
import {
  CellCommentPopover,
  type OpenCommentCell,
} from './comments/CellCommentPopover';
import {
  useReviewCommentJump,
  type LocateCommentRow,
} from './comments/useReviewCommentJump';
import {
  cellEditorFrom,
  editorViolations,
  markEditor,
  recordAt,
  violationsToAnnounce,
  editStateRendererFor,
} from './as400GridValidation';
import {
  GRID_ROW_HEIGHT,
  useSpreadsheetGridHeight,
} from './spreadsheetGridViewport';
import { As400ExportTableSkeleton } from './As400ExportTableSkeleton';
import { BeforeAfterToggle, type StagedView } from './BeforeAfterToggle';

registerAllModules();

/** The grid's source records plus the inputs they were built from. */
interface GridDataState {
  records: ReturnType<typeof buildGridRecords>;
  rows: As400ExportRow[];
  view: StagedView;
  dataVersion: number;
  lookups?: CellRowLookups;
}

// A column whose code is `__`-prefixed (e.g. the AS/400 view's `__source__`) is
// synthetic metadata — always read-only, never staged.
function isSyntheticColumn(code: string | null): boolean {
  return !!code && code.startsWith('__');
}

/** The fields a grid over `columns` can edit: every non-synthetic edit key. */
function editableFieldsOf(
  columns: readonly As400ExportColumn[],
): ReadonlySet<string> {
  const fields = new Set<string>();
  for (const column of columns) {
    const editKey = as400ColumnEditKey(column);
    if (editKey && !isSyntheticColumn(editKey)) fields.add(editKey);
  }
  return fields;
}

/**
 * The worksheet grid's leading CDB column: the company/division/branch the row
 * belongs to, concatenated (`WJH`) — read off the ingest record behind it, or
 * the division's own code on an authored row, which has no record yet. The
 * worksheets are per-division upload files, so their own columns never say
 * which branch a row is — with several divisions on screen at once (or one
 * division spanning several branches) an item's rows are otherwise
 * indistinguishable.
 *
 * Synthetic and so read-only, and frozen at the front (see
 * {@link worksheetFixedColumnCount}) so it stays put while the worksheet's
 * columns scroll horizontally.
 */
const CDB_GRID_COLUMN: As400ExportColumn = {
  header: 'CDB',
  code: AS400_CDB_COLUMN,
  comment:
    'Company, division and branch the row belongs to.\nNot part of the upload file.',
};

/**
 * Each worksheet's grid columns: the CDB key ahead of the worksheet's own.
 * Built once, so a worksheet's column array — which the grid's settings and
 * cell rules derive from — is the same object on every render.
 */
const WORKSHEET_GRID_COLUMNS = new Map<
  As400ExportGroup,
  readonly As400ExportColumn[]
>(
  AS400_EXPORT_GROUP_ORDER.map((group) => [
    group,
    [CDB_GRID_COLUMN, ...AS400_EXPORT_WORKSHEET_COLUMNS[group]],
  ]),
);

function worksheetGridColumns(
  group: As400ExportGroup,
): readonly As400ExportColumn[] {
  return WORKSHEET_GRID_COLUMNS.get(group) ?? [CDB_GRID_COLUMN];
}

/**
 * A worksheet's pending-edit scope. The change set is part of it: the route
 * keeps the section mounted when only the review changes, and edits drafted
 * in one review must never render in — or save into — another.
 */
function worksheetScopeKey(
  changeSetId: string,
  group: As400ExportGroup,
  dataSourceIds: readonly string[],
): string {
  return `${worksheetScopePrefix(changeSetId, group)}${dataSourceIds.join(',')}`;
}

function worksheetScopePrefix(
  changeSetId: string,
  group: As400ExportGroup,
): string {
  return `${changeSetId}|${group}|`;
}

/**
 * Every worksheet's pending-edit scope under the same review and divisions,
 * in tab order — where the error summary lists unsaved edits from.
 */
function worksheetErrorScopes(
  changeSetId: string,
  dataSourceIds: readonly string[],
): ErrorScope[] {
  return AS400_EXPORT_GROUP_ORDER.map((group) => ({
    scopeKey: worksheetScopeKey(changeSetId, group, dataSourceIds),
    group,
    columns: worksheetGridColumns(group),
    itemColumn: AS400_EXPORT_GROUP_SEARCH_COLUMN[group],
  }));
}

/**
 * How many leading columns stay frozen while the worksheet's own columns
 * scroll: the CDB key and everything up to and including the item-number
 * column, so which record a row is stays readable at any scroll offset. The
 * item number is every worksheet's first column, so this is two in practice —
 * found rather than assumed, so a worksheet that led with something else would
 * still keep its item number in view. Without one, the CDB alone is frozen.
 */
function worksheetFixedColumnCount(
  columns: readonly As400ExportColumn[],
  group: As400ExportGroup,
): number {
  const itemColumn = AS400_EXPORT_GROUP_SEARCH_COLUMN[group];
  const index = columns.findIndex(
    (column) => as400ColumnEditKey(column) === itemColumn,
  );
  return index === -1 ? 1 : index + 1;
}

/**
 * What a column sorts by: its edit key — the CDB key included, a spacer
 * column never.
 */
function columnSortKey(column: As400ExportColumn | undefined): string | null {
  return column ? as400ColumnEditKey(column) : null;
}

// Up to this many parts are named in the "hide all rows of…" menu item; a
// bigger selection is counted instead, so the menu stays one line.
const MAX_NAMED_ITEMS = 3;

/** The context-menu label that hides every row of the selection's parts. */
function hideItemsMenuLabel(
  gt: ReturnType<typeof useGT>,
  itemNumbers: readonly string[],
): string {
  if (itemNumbers.length > MAX_NAMED_ITEMS) {
    return gt('Hide all rows of these {count} Part Numbers', {
      count: itemNumbers.length,
      context:
        'grid context menu: hide every row of the many parts in the selection',
    });
  }
  // The quotes travel in the variable: in the message itself a quote is ICU's
  // escape, and would render the placeholder as literal text.
  return gt('Hide all {items} rows', {
    items: itemNumbers.map((itemNumber) => `'${itemNumber}'`).join(', '),
    context:
      "grid context menu: hide every row of the selection's parts, named by item number in quotes",
  });
}

/**
 * A grid column's binding key — the source field it reads and writes, and what
 * a remembered column width is filed under. Keyless (spacer) columns are keyed
 * by position, which is all they have.
 */
function columnDataKey(column: As400ExportColumn, index: number): string {
  return as400ColumnEditKey(column) ?? `__blank_${index}`;
}

/** An authored row's place in the canonical worksheet order. */
function newRowOrderKey(
  row: As400ExportRow,
  itemColumn: string,
): As400RowOrderKey {
  return {
    itemNumber: asCellText(row.stagedValues?.[itemColumn]),
    cdb: row.values[AS400_CDB_COLUMN] ?? null,
  };
}

/**
 * A draft new row: authored in the Add-row dialog, still pure local state. Its
 * division and location are remembered here because they aren't grid columns —
 * they are the row's CDB, and what the add is submitted with.
 */
/** The key of a scope's always-present blank new row. */
function entryRowKey(scopeKey: string): string {
  return draftRowKey(`entry:${scopeKey}`);
}

interface DraftNewRow {
  key: string;
  /**
   * The division the row starts under; null for a row added from the grid
   * with no division in view to assume, whose CDB is typed into it.
   */
  dataSourceId: string | null;
  /** One of RD's locations, or null for a division-wide row. */
  branch: string | null;
}

/**
 * Lets controls outside the grid (toolbar dialogs) pull focus away from it.
 * With `outsideClickDeselects` off, Handsontable keeps its open cell editor
 * focused through clicks elsewhere — so anything that opens a modal must first
 * ask the grid to let go, or typing lands in the cell behind the dialog.
 */
export interface As400GridFocusHandle {
  /** Commit any open cell editor and release the grid's keyboard focus. */
  blur: () => void;
  /** Whether a cell editor is open — Escape then belongs to the editor. */
  isEditing: () => boolean;
  /**
   * Select and scroll to one row's cell, leaving keyboard focus where it is
   * (the error list keeps it). False when the row or column isn't on screen.
   */
  selectCell: (rowKey: string, field: string) => boolean;
  /** Select one row's cell and take keyboard focus, ready to type into it. */
  editCell: (rowKey: string, field: string) => boolean;
  /**
   * Select one row's cell and open its comments popover (a jump to a
   * comment). False when the cell isn't on screen or takes no comments.
   */
  showComments: (rowKey: string, field: string) => boolean;
}

/** Row-level actions for authored new rows, surfaced via the context menu. */
export interface NewRowGridActions {
  /** Already-translated menu label (Handsontable renders outside React). */
  menuLabel: string;
  onDelete: (rowKey: string) => void;
}

/**
 * Lets controls outside the grid seed or drop pending cell edits — how the
 * Add-row dialog makes a draft row enter the same unsaved-edits → Save flow
 * as typed cells, instead of persisting on its own. Callers pass the scope
 * explicitly so async work (a save resolving after the user switched tabs)
 * still touches the scope it was submitted from, not whichever is active.
 */
export interface As400GridPendingActions {
  /** Seed one row's pending cell edits (replacing any it already has). */
  stageRowValues: (
    scopeKey: string,
    rowKey: string,
    values: Record<string, string | null>,
  ) => void;
  /** Drop one row's pending edits (a removed or just-saved draft row). */
  clearRowEdits: (scopeKey: string, rowKey: string) => void;
  /** Whether a row has any pending edits — whether a draft row was typed in. */
  hasRowEdits: (scopeKey: string, rowKey: string) => boolean;
}

type MenuItem = Handsontable.plugins.ContextMenu.MenuItemConfig;

/**
 * Hiding worksheet columns from the grid's context menu (GEN-2715). The frozen
 * leading columns (CDB, item number) say which record a row is, so they can't
 * be hidden.
 */
export interface GridColumnHiding {
  /** Already-translated menu label (Handsontable renders outside React). */
  menuLabel: string;
  /** The grid column keys ({@link columnDataKey}) hidden on this worksheet. */
  hidden: ReadonlySet<string>;
  onHide: (columnKeys: string[]) => void;
}

/**
 * The column keys of the selected cells, the frozen columns left out. A
 * selection made from the row gutter (whole rows) names no columns.
 */
function selectedHideableColumns(
  instance: Handsontable,
  fixedColumns: number,
): string[] {
  const keys = new Set<string>();
  for (const range of instance.getSelectedRange() ?? []) {
    const first = Math.min(range.from.col, range.to.col);
    if (first < 0) continue;
    const to = Math.max(range.from.col, range.to.col);
    for (let col = Math.max(first, fixedColumns); col <= to; col += 1) {
      const prop = instance.colToProp(col);
      if (typeof prop === 'string') keys.add(prop);
    }
  }
  return [...keys];
}

/**
 * The page indexes of the selected rows. A selection made from a column
 * header (whole columns) names no rows.
 */
function selectedRows(instance: Handsontable): number[] {
  const rows = new Set<number>();
  for (const range of instance.getSelectedRange() ?? []) {
    const from = Math.min(range.from.row, range.to.row);
    if (from < 0) continue;
    const to = Math.max(range.from.row, range.to.row);
    for (let row = from; row <= to; row += 1) rows.add(row);
  }
  return [...rows];
}

/**
 * What the context menu's items do. Read through a ref at click time, so the
 * menu setting itself stays the same object across renders: the wrapper
 * pushes a changed setting through `updateSettings`, which rebuilds the menu
 * plugin, and rebuilding it from inside its own callback breaks it.
 */
interface ContextMenuHandlers {
  newRowActions: NewRowGridActions | undefined;
  hideColumns: ((columnKeys: string[]) => void) | undefined;
  rowHiding: RowHidingActions | undefined;
  comments: CellCommentMenu | undefined;
}

/** Commenting on the selection's active cell (see useCellComments). */
interface CellCommentMenu {
  /** "Leave a comment" or "Show comment"; null where the cell takes none. */
  label: (instance: Handsontable) => string | null;
  open: (instance: Handsontable) => void;
}

/**
 * The grid's context menu: "reset" for the selection's unsaved edits,
 * "clear out" to each selected cell's AS/400 blank-out marker,
 * "remove" on authored new rows, "hide columns" on any selection that spans
 * an unfrozen column, and on any selection of rows (worksheets only) "hide
 * selected rows" plus "hide all '…' rows" for the selection's parts, named as
 * the menu opens, and with `withComments` "leave a comment" (or "show comment")
 * on a cell that can take one. Handsontable invokes the callbacks with the
 * instance as `this`.
 */
function buildContextMenu(
  handlers: RefObject<ContextMenuHandlers>,
  labels: {
    resetToSaved: string;
    resetToAs400: string;
    blankOutCells: string;
    removeNewRow: string | undefined;
    hideColumns: string | undefined;
    hideRows: string | undefined;
  },
  fixedColumns: number,
  blankMarkerFor: BlankMarkerFor,
  withComments: boolean,
): Handsontable.GridSettings['contextMenu'] {
  const items: Record<string, MenuItem> = {
    reset_to_saved: resetToSavedItem(labels.resetToSaved),
    reset_to_as400: resetToAs400Item(labels.resetToAs400),
    blank_out_cells: blankOutCellsItem(labels.blankOutCells, blankMarkerFor),
  };
  if (labels.removeNewRow) {
    items.remove_new_row = removeNewRowItem(labels.removeNewRow, handlers);
  }
  if (labels.hideColumns) {
    items.hide_columns = {
      name: labels.hideColumns,
      hidden(this: Handsontable) {
        return selectedHideableColumns(this, fixedColumns).length === 0;
      },
      callback(this: Handsontable) {
        handlers.current.hideColumns?.(
          selectedHideableColumns(this, fixedColumns),
        );
      },
    };
  }
  if (labels.hideRows) {
    items.hide_rows = {
      name: labels.hideRows,
      hidden(this: Handsontable) {
        return selectedRows(this).length === 0;
      },
      callback(this: Handsontable) {
        handlers.current.rowHiding?.hideSelected(selectedRows(this));
      },
    };
    const itemsLabel = (instance: Handsontable) =>
      handlers.current.rowHiding?.hideItemsLabel(selectedRows(instance)) ??
      null;
    items.hide_item_rows = {
      name(this: Handsontable) {
        return itemsLabel(this) ?? '';
      },
      hidden(this: Handsontable) {
        return itemsLabel(this) === null;
      },
      callback(this: Handsontable) {
        handlers.current.rowHiding?.hideItems(selectedRows(this));
      },
    };
  }
  if (withComments) items.cell_comment = cellCommentItem(handlers);
  return { items };
}

/** The comment item, named for the cell as the menu opens. */
function cellCommentItem(handlers: RefObject<ContextMenuHandlers>): MenuItem {
  const label = (instance: Handsontable) =>
    handlers.current.comments?.label(instance) ?? null;
  return {
    name(this: Handsontable) {
      return label(this) ?? '';
    },
    hidden(this: Handsontable) {
      return label(this) === null;
    },
    callback(this: Handsontable) {
      handlers.current.comments?.open(this);
    },
  };
}

/**
 * The "clear out" item: every writable selected cell, across all ranges, gets
 * its own column's AS/400 blank-out marker (`/////` for ICAT1, `/` for
 * ICODE4…) — what the upload reads as "blank this field", where an empty cell
 * means "do not change". Cells whose column documents no marker are skipped;
 * greyed out when none of the selection has one. Delete does the same (see
 * blankOutOnDelete).
 */
function blankOutCellsItem(
  menuLabel: string,
  blankMarkerFor: BlankMarkerFor,
): MenuItem {
  return {
    name: menuLabel,
    disabled(this: Handsontable) {
      return (
        cellBlankOuts(selectedCells(this), writableCellAt(this), blankMarkerFor)
          .length === 0
      );
    },
    callback(this: Handsontable) {
      blankOutSelection(this, blankMarkerFor);
    },
  };
}

/** The "remove" item, shown only on authored new rows. */
function removeNewRowItem(
  menuLabel: string,
  handlers: RefObject<ContextMenuHandlers>,
): MenuItem {
  const selectedRowKey = (instance: Handsontable): string | null => {
    const selection = instance.getSelectedLast();
    if (!selection) return null;
    const record = instance.getSourceDataAtRow(
      instance.toPhysicalRow(selection[0]),
    );
    return isGridRecord(record) ? record.__key : null;
  };
  const isNewRow = (rowKey: string | null): rowKey is string =>
    rowKey !== null && (isNewRowKey(rowKey) || isDraftRowKey(rowKey));
  return {
    name: menuLabel,
    hidden(this: Handsontable) {
      return !isNewRow(selectedRowKey(this));
    },
    callback(this: Handsontable) {
      const rowKey = selectedRowKey(this);
      if (isNewRow(rowKey)) handlers.current.newRowActions?.onDelete(rowKey);
    },
  };
}

/**
 * A column's worksheet note plus the viewport rect of the header cell it
 * belongs to. The rect positions an invisible tooltip trigger over that cell,
 * which is how the shared <Tooltip> anchors to a header Handsontable renders
 * outside React (Radix's tooltip has no standalone Anchor).
 */
interface HeaderNote {
  text: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

// Marks a header cell as having a note, holding the index to look it up by.
const NOTE_COLUMN_ATTR = 'data-column-note';

// Excel-style corner triangle marking a header that carries a worksheet note —
// the same affordance the source workbook uses for a commented cell. Inline
// styles throughout: Handsontable renders headers outside Tailwind's pipeline.
const NOTE_MARKER_CLASS = 'as400-note-marker';
const NOTE_MARKER_SIZE = '6px';
const NOTE_MARKER_COLOR = 'var(--primary)';

/** Draw the note marker on a header cell, once. */
function addNoteMarker(th: HTMLTableCellElement): void {
  // Handsontable wraps header content in `div.relative`; the marker is a
  // sibling of the label span, so re-rendering the label leaves it in place.
  const host = th.firstElementChild ?? th;
  if (host.querySelector(`.${NOTE_MARKER_CLASS}`)) return;
  const marker = document.createElement('span');
  marker.className = NOTE_MARKER_CLASS;
  marker.setAttribute('aria-hidden', 'true');
  Object.assign(marker.style, {
    position: 'absolute',
    top: '0',
    right: '0',
    width: '0',
    height: '0',
    borderTop: `${NOTE_MARKER_SIZE} solid ${NOTE_MARKER_COLOR}`,
    borderLeft: `${NOTE_MARKER_SIZE} solid transparent`,
    pointerEvents: 'none',
  });
  host.appendChild(marker);
}

/** Strip the marker from a recycled header cell that no longer has a note. */
function removeNoteMarker(th: HTMLTableCellElement): void {
  th.querySelector(`.${NOTE_MARKER_CLASS}`)?.remove();
}

/** The header cell under `target` that carries a note, if any. */
function notedHeaderFrom(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof HTMLElement)) return null;
  return target.closest<HTMLElement>(`th[${NOTE_COLUMN_ATTR}]`);
}

/**
 * The note a marked header cell refers to, ending with the column's blank-out
 * marker spelled out when it has one — so it can be read off, not counted
 * from the worksheet's prose.
 */
function noteTextFor(
  columns: readonly As400ExportColumn[],
  th: HTMLElement,
  blankOutLine: (marker: string) => string,
): string | undefined {
  const column = columns[Number(th.getAttribute(NOTE_COLUMN_ATTR))];
  if (!column?.comment || !column.blankMarker) return column?.comment;
  return `${column.comment}\n\n${blankOutLine(column.blankMarker)}`;
}

/** Viewport box of the hovered header cell — a zero box when none is hovered. */
function anchorBox(note: HeaderNote | null): React.CSSProperties {
  if (!note) return { left: 0, top: 0, width: 0, height: 0 };
  const { left, top, width, height } = note;
  return { left, top, width, height };
}

/**
 * The note bubble. Its open state is driven by the hovered header rather than by
 * the trigger, which is an invisible stand-in laid over that cell purely so
 * Radix has something to anchor and point its arrow at. `pointer-events-none`
 * keeps it out of the grid's own mouse handling — including the delegated
 * mousemove that opens it.
 */
function HeaderNoteTooltip({
  store,
  side = 'top',
  id,
}: {
  store: HeaderNoteStore;
  side?: 'top' | 'bottom';
  /** For an element to name as described by the note (`aria-describedby`). */
  id?: string;
}) {
  const note = useSyncExternalStore(store.subscribe, store.get);
  return (
    <Tooltip open={note !== null}>
      <TooltipTrigger asChild>
        <span
          aria-hidden="true"
          className="pointer-events-none fixed"
          style={anchorBox(note)}
        />
      </TooltipTrigger>
      {note ? (
        <TooltipContent
          id={id}
          side={side}
          sideOffset={6}
          className="max-w-[320px] text-left whitespace-pre-line"
        >
          {note.text}
        </TooltipContent>
      ) : undefined}
    </Tooltip>
  );
}

/**
 * The hovered header's note, held outside React state. Setting state in the
 * grid on hover would re-render it, and the Handsontable wrapper pushes every
 * prop through `updateSettings` on each render — which re-initialises the
 * ContextMenu plugin and closes an open menu. So pointing from a noted header
 * (the frozen CDB column has one) toward its menu used to close the menu.
 * Only the tooltip subscribes to this, so only the tooltip re-renders.
 */
type HeaderNoteStore = ValueStore<HeaderNote | null>;

function createHeaderNoteStore(): HeaderNoteStore {
  return createValueStore<HeaderNote | null>(null);
}

/**
 * The column-note affordance for a Handsontable grid: marks the header cells
 * that carry a worksheet note, tracks which one the pointer is over, and
 * renders the tooltip anchored to it.
 *
 * Handsontable renders headers outside React, so the marking is imperative and
 * the tooltip is driven by a delegated mousemove rather than by a real trigger.
 * A native `title` is not an option — the attribute carried the note but the
 * browser never surfaced it over Handsontable's header overlays.
 */
function useHeaderNotes(columns: readonly As400ExportColumn[]) {
  const [store] = useState(createHeaderNoteStore);
  const hovered = useRef<HTMLElement | null>(null);
  const gt = useGT();
  const blankOutLine = (marker: string) =>
    gt('Blank-out value: {marker}', {
      marker,
      context:
        'review grid column header tooltip: the run of slashes that blanks this field on the AS/400 upload',
    });

  // Handsontable recycles header cells across redraws, so each is always either
  // marked or cleared — never left carrying a previous column's marker.
  const markHeader = (
    colIndex: number,
    th: HTMLTableCellElement,
    noted = true,
  ) => {
    if (noted && columns[colIndex]?.comment) {
      th.setAttribute(NOTE_COLUMN_ATTR, String(colIndex));
      addNoteMarker(th);
      return;
    }
    th.removeAttribute(NOTE_COLUMN_ATTR);
    removeNoteMarker(th);
  };

  // Tracking the hovered cell in a ref keeps mousemove from notifying the
  // tooltip until the cell actually changes.
  const handleMouseMove = (event: React.MouseEvent<HTMLElement>) => {
    const th = notedHeaderFrom(event.target);
    if (th === hovered.current) return;
    hovered.current = th;

    const text = th ? noteTextFor(columns, th, blankOutLine) : undefined;
    if (!th || !text) {
      store.set(null);
      return;
    }
    const { left, top, width, height } = th.getBoundingClientRect();
    store.set({ text, left, top, width, height });
  };

  // Leaving the grid, or scrolling under a still pointer, would otherwise leave
  // the tooltip pinned to a cell that has moved.
  const clear = () => {
    hovered.current = null;
    store.set(null);
  };

  return {
    markHeader,
    handleMouseMove,
    clear,
    tooltip: <HeaderNoteTooltip store={store} />,
  };
}

/**
 * Cell comments on the review grid (GEN-3263): what a grid shows comments for
 * and where they live. Omitted (the item catalog) = no comments.
 */
export interface GridCellComments {
  changeSetId: string;
  group: As400ExportGroup;
  /** The review's threads, by `anchor.cellKey` ({@link indexThreadsByCell}). */
  threadsByCell: CellThreadIndex;
}

/** A commentable cell at visual coordinates, with its threads. */
interface CommentCell {
  rowKey: string;
  columnKey: string;
  threads: ChangeSetCommentThread[];
}

/** The commentable cell at visual `row`/`col`; null where none can be. */
function commentCellAt(
  instance: Handsontable,
  row: number,
  col: number,
  comments: GridCellComments | undefined,
): CommentCell | null {
  const record = row < 0 || col < 0 ? null : recordAt(instance, row);
  const columnKey = instance.colToProp(col);
  if (!comments || !record || typeof columnKey !== 'string') return null;
  if (!isCommentableCell(comments.group, record.__key, columnKey)) return null;
  return {
    rowKey: record.__key,
    columnKey,
    threads: threadsForCell(
      comments.threadsByCell,
      comments.group,
      record.__key,
      columnKey,
    ),
  };
}

/**
 * Paint a cell's comment marker as it renders (see the grid's renderer).
 * Runs for every cell drawn, so it only looks the cell up — a row or column
 * that can't take a comment has none to find.
 */
function markCommentedCell(
  instance: Handsontable,
  td: HTMLTableCellElement,
  row: number,
  col: number,
  comments: GridCellComments | undefined,
): void {
  const record =
    comments && comments.threadsByCell.size > 0
      ? recordAt(instance, row)
      : null;
  const columnKey = instance.colToProp(col);
  paintCommentMarker(
    td,
    comments && record && typeof columnKey === 'string'
      ? cellCommentMarker(
          threadsForCell(
            comments.threadsByCell,
            comments.group,
            record.__key,
            columnKey,
          ),
        )
      : null,
  );
}

/**
 * The cell-comment affordance for the review grid, built like the header
 * notes: a corner marker painted on each commented cell as it renders, a
 * read-only preview on hover, and the thread popover — opened from the
 * context menu, a click on the marker, or a jump ({@link showAt}).
 *
 * Handsontable renders cells outside React, and the renderer must stay one
 * function for the grid's lifetime (a new one is new column settings, which
 * redraws everything): so it reads the threads through a ref, and a changed
 * thread list redraws the grid instead. The preview and the popover are held
 * outside React state, as the header note is, so neither re-renders the grid.
 */
function useCellComments(
  hotRef: RefObject<HotTableRef | null>,
  comments: GridCellComments | undefined,
) {
  const gt = useGT();
  const [preview] = useState(createHeaderNoteStore);
  const [openCell] = useState(() =>
    createValueStore<OpenCommentCell | null>(null),
  );
  const hovered = useRef<HTMLTableCellElement | null>(null);
  const current = useRef(comments);
  useLayoutEffect(() => {
    current.current = comments;
  });
  const threadsByCell = comments?.threadsByCell;
  useEffect(() => {
    hotRef.current?.hotInstance?.render();
  }, [hotRef, threadsByCell]);

  const cellAt = (instance: Handsontable, row: number, col: number) =>
    commentCellAt(instance, row, col, current.current);

  // Where the open popover's cell is, so it can follow the cell as the grid
  // scrolls (Handsontable reuses its cells' elements, so not the element).
  const openAtCoords = useRef<{ row: number; col: number } | null>(null);
  useEffect(() => {
    const instance = hotRef.current?.hotInstance;
    if (!instance) return;
    const follow = () => {
      const open = openCell.get();
      const at = openAtCoords.current;
      if (!open || !at) return;
      const rect = followedCellRect(
        instance.getCell(at.row, at.col)?.getBoundingClientRect() ?? null,
        instance.rootElement.getBoundingClientRect(),
      );
      if (rect) {
        openCell.set({ ...open, rect });
        return;
      }
      // Scrolled out of view: close, and hand the keyboard back to the grid
      // without selecting the cell again (that would scroll back to it).
      openCell.set(null);
      instance.listen();
    };
    instance.addHook('afterScrollVertically', follow);
    instance.addHook('afterScrollHorizontally', follow);
    return () => {
      if (instance.isDestroyed) return;
      instance.removeHook('afterScrollVertically', follow);
      instance.removeHook('afterScrollHorizontally', follow);
    };
  });

  /** Open the popover on the cell at visual `row`/`col`; false if it can't. */
  const openAt = (instance: Handsontable, row: number, col: number) => {
    const cell = cellAt(instance, row, col);
    const td = instance.getCell(row, col, true);
    const scope = current.current;
    const record = recordAt(instance, row);
    if (!cell || !td || !scope || !record) return false;
    // The popover's text box takes the keyboard: commit any open cell editor
    // and stop the grid listening, or typing lands in the cell behind.
    instance.getActiveEditor()?.finishEditing();
    instance.unlisten();
    hovered.current = null;
    preview.set(null);
    td.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const { left, top, width, height } = td.getBoundingClientRect();
    openAtCoords.current = { row, col };
    openCell.set({
      rowKey: cell.rowKey,
      columnKey: cell.columnKey,
      itemNumber: asCellText(
        record[AS400_EXPORT_GROUP_SEARCH_COLUMN[scope.group]],
      ),
      cdb: asCellText(record[AS400_CDB_COLUMN]),
      rect: { left, top, width, height },
    });
    return true;
  };

  /** Open the popover on the selection's active cell. */
  const openAtSelection = (instance: Handsontable) => {
    const highlight = instance.getSelectedRangeLast()?.highlight;
    return highlight ? openAt(instance, highlight.row, highlight.col) : false;
  };

  const leaveLabel = gt('Leave a comment', {
    context: 'review grid context menu: start a comment thread on the cell',
  });
  const showLabel = gt('Show comment', {
    context: "review grid context menu: open the cell's comment threads",
  });
  const menu: CellCommentMenu = {
    label(instance) {
      const highlight = instance.getSelectedRangeLast()?.highlight;
      const cell = highlight && cellAt(instance, highlight.row, highlight.col);
      if (!cell) return null;
      return cell.threads.length > 0 ? showLabel : leaveLabel;
    },
    open: openAtSelection,
  };

  /** The commented cell an event is on, with the grid it belongs to. */
  const commentedCell = (target: EventTarget | null) => {
    const td = commentedCellFrom(target);
    const instance = hotRef.current?.hotInstance;
    const coords = td && instance?.getCoords(td);
    return td && instance && coords ? { td, instance, coords } : null;
  };

  /** The read-only preview of a commented cell's threads, placed over it. */
  const previewOf = ({
    td,
    instance,
    coords,
  }: NonNullable<ReturnType<typeof commentedCell>>): HeaderNote | null => {
    const threads = cellAt(instance, coords.row, coords.col)?.threads ?? [];
    if (threads.length === 0) return null;
    const { left, top, width, height } = td.getBoundingClientRect();
    return { text: commentPreviewText(threads), left, top, width, height };
  };

  // Tracking the hovered cell in a ref keeps mousemove from notifying the
  // preview until the cell actually changes; none while the popover is open.
  const handleMouseMove = (event: React.MouseEvent<HTMLElement>) => {
    const target = commentedCell(event.target);
    const td = target?.td ?? null;
    if (td === hovered.current) return;
    hovered.current = td;
    preview.set(target && !openCell.get() ? previewOf(target) : null);
  };

  // A click on a cell's marker opens its comments; anywhere else in the cell
  // only selects it, as in any spreadsheet.
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    const target = commentedCell(event.target);
    if (
      !target ||
      !isOnCommentMarker(target.td.getBoundingClientRect(), event)
    ) {
      return;
    }
    openAt(target.instance, target.coords.row, target.coords.col);
  };

  const clear = () => {
    hovered.current = null;
    preview.set(null);
  };

  // Closing the comments hands the keyboard back to the grid: selecting the
  // active cell again makes the grid listen, so arrows and typing carry on
  // from the cell the comments were on.
  const returnToGrid = () => {
    const instance = hotRef.current?.hotInstance;
    const active = instance?.getSelectedRangeLast()?.highlight;
    if (instance && active) instance.selectCell(active.row, active.col);
  };

  return {
    /** The comments the renderer marks cells from (see markCommentedCell). */
    scope: current,
    menu,
    /** Whether a cell's comments popover is open. */
    isOpen: () => openCell.get() !== null,
    /** Select one row's cell and open its comments (a jump). */
    showAt(rowKey: string, field: string) {
      const instance = hotRef.current?.hotInstance;
      if (!instance || !selectRecordCell(instance, rowKey, field)) {
        return false;
      }
      return openAtSelection(instance);
    },
    handleMouseMove,
    handleClick,
    clear,
    overlays: comments ? (
      <>
        <HeaderNoteTooltip store={preview} />
        <CellCommentPopover
          store={openCell}
          changeSetId={comments.changeSetId}
          group={comments.group}
          threadsByCell={comments.threadsByCell}
          onClosed={returnToGrid}
        />
      </>
    ) : undefined,
  };
}

/**
 * The live check on the open cell editor (GEN-2723): each keystroke or paste
 * into it is checked against the cell's rules as if committed, and a broken
 * one is said at once — the editor turns red and a note under it says what to
 * fix, updated as the value changes — rather than only once the cell is left.
 * The rules are local functions; nothing here goes to the server.
 *
 * The note is held the way a header note is (see useHeaderNotes): outside
 * React state, so typing never re-renders the grid.
 */
function useEditorCheck(
  hotRef: RefObject<HotTableRef | null>,
  rules: CellRulesByField,
  autoFills: readonly CellAutoFill[],
  describe: (violation: CellViolation) => string,
  blankMarkerFor: BlankMarkerFor,
) {
  const [store] = useState(createHeaderNoteStore);
  const noteId = useId();

  const handleInput = (event: React.FormEvent<HTMLElement>) => {
    const editor = cellEditorFrom(event.target);
    const instance = hotRef.current?.hotInstance;
    if (!editor || !instance) return;
    const violations = editorViolations(
      instance,
      rules,
      editor.value,
      autoFills,
      blankMarkerFor,
    );
    if (violations.length === 0) {
      markEditor(editor, null);
      store.set(null);
      return;
    }
    markEditor(editor, noteId, violations.some(isBlocking));
    const { left, top, width, height } = editor.getBoundingClientRect();
    store.set({
      text: violations.map(describe).join('\n'),
      left,
      top,
      width,
      height,
    });
  };

  // A scroll takes the note and the outline away — the editor may have moved
  // under a frozen column, where a note pointing at it would be wrong — and
  // the next keystroke brings both back; a commit in between gets the
  // leave-cell popup, so the problem is always said once. The grid scrolls
  // inside Handsontable's own holder and the page around it, and neither
  // reaches the wrapper (scroll doesn't bubble), hence a capturing listener
  // on the document.
  useEffect(() => {
    const clear = () => {
      if (!store.get()) return;
      const editor = cellEditorFrom(
        hotRef.current?.hotInstance?.rootElement.querySelector(
          '.handsontableInput',
        ) ?? null,
      );
      if (editor) markEditor(editor, null);
      store.set(null);
    };
    document.addEventListener('scroll', clear, true);
    return () => document.removeEventListener('scroll', clear, true);
  }, [hotRef, store]);

  // The editor closing — on commit, Escape or a click elsewhere — takes the
  // outline and the note with it.
  const handleBlur = (event: React.FocusEvent<HTMLElement>) => {
    const editor = cellEditorFrom(event.target);
    if (!editor) return;
    markEditor(editor, null);
    store.set(null);
  };

  return {
    handleInput,
    handleBlur,
    /**
     * Whether the editor's note is showing — the author has been told about
     * the value being committed. The editor commits before it closes, so a
     * commit reads this ahead of the blur.
     */
    noteShown: () => store.get() !== null,
    note: <HeaderNoteTooltip store={store} side="bottom" id={noteId} />,
  };
}

/** How column `index`'s header reads under the sort; undefined = no control. */
function sortToggleStateFor(
  sortKeys: readonly (string | null)[],
  sorted: SortedColumn | null,
  index: number,
): SortToggleState | undefined {
  if (!sortKeys[index]) return undefined;
  return sorted?.index === index ? sorted.direction : 'none';
}

/**
 * Redraws the headers when the sorted column changes, so every header's
 * chevron reads the new state (see renderSortToggle). Handsontable only
 * re-runs the header hook on a render.
 */
function useSortedHeaders(
  hotRef: RefObject<HotTableRef | null>,
  sorted: SortedColumn | null,
): void {
  useEffect(() => {
    hotRef.current?.hotInstance?.render();
  }, [hotRef, sorted]);
}

/**
 * Keep the grid's hidden rows and columns in step with what the review says
 * is hidden, through the plugins' API (see as400GridHiding). Runs after every
 * data swap too, which resets the plugins' maps.
 */
function useGridHiding(
  hotRef: RefObject<HotTableRef | null>,
  records: readonly unknown[],
  hiddenRowIndexes: readonly number[],
  columns: readonly As400ExportColumn[],
  hiddenColumnKeys: ReadonlySet<string> | undefined,
): void {
  const hiddenColumnIndexes = useMemo(
    () =>
      columns.flatMap((column, index) =>
        hiddenColumnKeys?.has(columnDataKey(column, index)) ? [index] : [],
      ),
    [columns, hiddenColumnKeys],
  );
  useEffect(() => {
    const instance = hotRef.current?.hotInstance;
    if (!instance) return;
    enableGridHiding(instance);
    applyGridHiding(instance, hiddenRowIndexes, hiddenColumnIndexes);
  }, [hotRef, records, hiddenRowIndexes, hiddenColumnIndexes]);
}

/**
 * The fill handle's drag preview, made visible. Handsontable draws it, but as
 * a 1px border in `--ht-cell-autofill-fill-border-color` — a mid grey when no
 * theme sets it — around the cells the drag will fill, and leaves the `fill`
 * class it puts on those cells unstyled. On this grid that border is the same
 * weight and near enough the colour of the gridlines it lies on, so the
 * preview is drawn and can't be seen. Colour the border like the selection's
 * and tint the covered cells the way Handsontable tints a selected range
 * (`td.area::before`): an overlay, so the cells' own edit-state tints (opaque
 * inline backgrounds) don't hide it.
 */
const FILL_PREVIEW_CLASSES = cn(
  '[--ht-cell-autofill-fill-border-color:var(--ht-cell-selection-border-color,#1a42e8)]',
  '[&_td.fill]:relative',
  "[&_td.fill]:before:pointer-events-none [&_td.fill]:before:absolute [&_td.fill]:before:inset-0 [&_td.fill]:before:content-['']",
  '[&_td.fill]:before:bg-[var(--ht-cell-selection-background-color,#1a42e8)] [&_td.fill]:before:opacity-15',
);

/**
 * The spreadsheet-style grid for one page of a flattened AS/400 view. In an
 * editable (change-set) view's After mode, cells show the effective value
 * (pending edit, else staged edit, else live value) and are typed into directly
 * — no inner text boxes; multi-cell ranges copied from Excel/Google Sheets paste
 * across the grid natively (Handsontable's CopyPaste plugin). The Before view
 * and the fully `readOnly` (item-catalog) view are the same table with the live
 * values in place and no editing — copy still works.
 */
function As400EditableExportGrid({
  columns,
  rows,
  pending,
  view,
  onCellEdited,
  dataVersion,
  readOnly,
  fixedColumns,
  columnWidths,
  onColumnResized,
  newRowActions,
  focusRef,
  sorting,
  rowHiding,
  columnHiding,
  takenItemNumbers,
  lookups,
  comments,
}: GridBodyProps & { rows: As400ExportRow[] }) {
  const hotRef = useRef<HotTableRef>(null);

  // Derived per row set / hidden change, never per keystroke: typing changes
  // neither. Row indexes are the grid's own — it never reorders rows, and
  // hiding keeps visual indexes as they are.
  const hiddenRowIndexesOnPage = useMemo(
    () =>
      rowHiding
        ? hiddenRowIndexes(rows, rowHiding.itemColumn, rowHiding.hidden)
        : [],
    [rows, rowHiding],
  );

  const cellComments = useCellComments(hotRef, comments);
  const menuHandlers = useRef<ContextMenuHandlers>({
    newRowActions: undefined,
    hideColumns: undefined,
    rowHiding: undefined,
    comments: undefined,
  });
  useEffect(() => {
    menuHandlers.current = {
      newRowActions,
      hideColumns: columnHiding?.onHide,
      rowHiding: rowHiding && rowHidingActions(rows, rowHiding),
      comments: cellComments.menu,
    };
  });
  const withComments = comments !== undefined;
  const removeNewRowLabel = newRowActions?.menuLabel;
  const hideColumnsLabel = columnHiding?.menuLabel;
  const hideRowsLabel = rowHiding?.menuLabels.hideSelected;
  const gt = useGT();
  const resetToSavedLabel = gt('Reset to last saved value', {
    context:
      'review grid context menu: drop the unsaved edits in the selected cells, putting back what was last saved (saved edits stay)',
  });
  const resetToAs400Label = gt('Reset to AS/400 value', {
    context:
      'review grid context menu: put the selected cells back to their live AS/400 values, discarding both unsaved and saved edits (takes effect on Save)',
  });
  const blankOutCellsLabel = gt('Clear out (blank on AS/400)', {
    context:
      "review grid context menu: fill the selected cells with each column's slash marker that tells the AS/400 upload to blank the field",
  });
  const blankMarkerFor = useMemo(() => blankMarkerLookup(columns), [columns]);
  const contextMenu = useMemo(
    () =>
      readOnly
        ? false
        : buildContextMenu(
            menuHandlers,
            {
              resetToSaved: resetToSavedLabel,
              resetToAs400: resetToAs400Label,
              blankOutCells: blankOutCellsLabel,
              removeNewRow: removeNewRowLabel,
              hideColumns: hideColumnsLabel,
              hideRows: hideRowsLabel,
            },
            fixedColumns,
            blankMarkerFor,
            withComments,
          ),
    [
      readOnly,
      resetToSavedLabel,
      resetToAs400Label,
      blankOutCellsLabel,
      removeNewRowLabel,
      hideColumnsLabel,
      hideRowsLabel,
      fixedColumns,
      blankMarkerFor,
      withComments,
    ],
  );

  const [wrapperRef, gridHeight] = useSpreadsheetGridHeight(
    rows.length - hiddenRowIndexesOnPage.length,
  );
  const headerNotes = useHeaderNotes(columns);
  // Each column's supplier on Purchase Data (see as400SupplierGroups).
  const suppliers = useMemo(() => supplierNumbersOf(columns), [columns]);
  const sortKeys = useMemo(() => columns.map(columnSortKey), [columns]);
  const sorted = useMemo(
    () =>
      sorting ? sortedColumn(sorting.sort, sorting.group, sortKeys) : null,
    [sorting, sortKeys],
  );
  useSortedHeaders(hotRef, sorted);

  // A click on a header's chevron asks for the review's sort — never a
  // header-label click, which selects the column as in any spreadsheet. The
  // grid never reorders rows itself: the server sorts, for every worksheet,
  // and edits stay keyed by row identity (`__key`).
  const handleSortToggleClick = (event: React.MouseEvent<HTMLElement>) => {
    const toggle = sortToggleFrom(event.target);
    if (!toggle || !sorting) return;
    const next = nextSortFor(
      sorting.sort,
      sorting.group,
      sortKeys,
      sortToggleColumn(toggle),
    );
    if (next !== undefined) sorting.onSortChange(next);
  };
  // The chevron is its own click target: the mousedown that reaches it must
  // not also select the column under it (a set flag disallows that area).
  const handleBeforeOnCellMouseDown: NonNullable<
    Handsontable.GridSettings['beforeOnCellMouseDown']
  > = (event, _coords, _td, controller) => {
    if (!sortToggleFrom(event.target)) return;
    controller.row = true;
    controller.column = true;
    controller.cell = true;
  };
  const { showToast } = useToast();

  // This view's cell rules (GEN-2748) — a worksheet's character limits,
  // number formats and its own rules (sheetRules/); the Queries views' raw
  // columns declare none — and the headers to name a broken one by. The
  // renderer outlines every cell that breaks one.
  const rules = useMemo(
    () => as400CellRules(columns, { takenItemNumbers }),
    [columns, takenItemNumbers],
  );
  // The worksheet's auto-fills, and the fields they may write: the editable
  // ones, as the column settings below decide.
  const autoFills = useMemo(() => as400CellAutoFills(columns), [columns]);
  const editableFields = useMemo(() => editableFieldsOf(columns), [columns]);
  const columnName = useMemo(() => columnNamer(columns), [columns]);
  const describeViolation = useCallback(
    (violation: CellViolation) =>
      describeCellViolation(gt, violation, columnName),
    [gt, columnName],
  );
  const spaceLabel = useCallback(
    (count: number) =>
      gt('{count, plural, one {# space} other {# spaces}}', {
        count,
        context:
          'review grid: what a run of shown spaces in a cell is read as by a screen reader',
      }),
    [gt],
  );
  const numberFormats = useMemo(
    () => as400ColumnNumberFormats(columns),
    [columns],
  );
  // The comments are read through a ref as each cell draws, so new threads
  // re-mark the cells without new column settings (see useCellComments).
  const commentScope = cellComments.scope;
  const renderer = useMemo(() => {
    const renderEditState = editStateRendererFor(
      rules,
      describeViolation,
      spaceLabel,
      numberFormats,
    );
    return (...args: Parameters<typeof renderEditState>) => {
      renderEditState(...args);
      const [instance, td, row, col] = args;
      markCommentedCell(instance, td, row, col, commentScope.current);
      paintSupplierCell(td, suppliers, col);
    };
  }, [
    rules,
    describeViolation,
    spaceLabel,
    numberFormats,
    commentScope,
    suppliers,
  ]);
  const editorCheck = useEditorCheck(
    hotRef,
    rules,
    autoFills,
    describeViolation,
    blankMarkerFor,
  );

  useImperativeHandle(focusRef, () => ({
    blur() {
      const instance = hotRef.current?.hotInstance;
      if (!instance) return;
      // Commit (not discard) whatever was mid-typing, then let go: deselect
      // and stop capturing the keyboard so a dialog's focus trap can win.
      instance.getActiveEditor()?.finishEditing();
      instance.deselectCell();
      instance.unlisten();
    },
    isEditing() {
      return (
        hotRef.current?.hotInstance?.getActiveEditor()?.isOpened() ?? false
      );
    },
    selectCell(rowKey, field) {
      const instance = hotRef.current?.hotInstance;
      return instance ? selectRecordCell(instance, rowKey, field) : false;
    },
    editCell(rowKey, field) {
      const instance = hotRef.current?.hotInstance;
      if (!instance || !selectRecordCell(instance, rowKey, field)) return false;
      instance.listen();
      return true;
    },
    showComments(rowKey, field) {
      return cellComments.showAt(rowKey, field);
    },
  }));

  // The grid's source records, held as state and rebuilt during render only
  // when a build input changes ("adjust state when props change"). `pending`
  // is snapshotted at build time rather than being a build trigger: a rebuild
  // on each keystroke would swap Handsontable's data array and reset the
  // selection. Handsontable keeps typed values itself; the snapshot only
  // matters when the records ARE rebuilt (page/group/view switch, or
  // `dataVersion` bumps after discard/save), which restores unsaved edits.
  const [gridState, setGridState] = useState<GridDataState>(() => ({
    records: buildGridRecords(
      rows,
      columns,
      view,
      pending,
      lookups?.itemAdds,
      lookups?.stockUoms,
    ),
    rows,
    view,
    dataVersion,
    lookups,
  }));
  if (
    gridRecordsNeedRebuild(gridState, {
      rows,
      view,
      dataVersion,
      lookups,
    })
  ) {
    setGridState({
      records: buildGridRecords(
        rows,
        columns,
        view,
        pending,
        lookups?.itemAdds,
        lookups?.stockUoms,
      ),
      rows,
      view,
      dataVersion,
      lookups,
    });
  }
  useGridHiding(
    hotRef,
    gridState.records,
    hiddenRowIndexesOnPage,
    columns,
    columnHiding?.hidden,
  );

  // One grid column per view column, bound by its edit key — the ingest field
  // code when mapped, the export-only column key otherwise. Keyless (spacer)
  // and synthetic columns have no field to write, so they stay read-only. The
  // Before view and the fully read-only (item-catalog) view are entirely
  // read-only — old values are history, not an editing surface.
  // Handsontable trims a typed value on commit (its `trimWhitespace`, on by
  // default) — right for text, where a stray space is a slip, and wrong for a
  // column the AS/400 reads by position, where a leading space is the value.
  // Those keep every space and render each one visibly (see the renderer).
  const columnSettings = useMemo(
    () =>
      columns.map((column, index) => {
        const editKey = as400ColumnEditKey(column);
        return {
          data: columnDataKey(column, index),
          readOnly:
            readOnly ||
            !editKey ||
            isSyntheticColumn(editKey) ||
            view === 'before',
          renderer,
          trimWhitespace: !column.keepWhitespace,
          className: as400ReviewGridColumnClassName(numberFormats, editKey),
        };
      }),
    [columns, readOnly, view, renderer, numberFormats],
  );
  const colHeaders = useMemo(
    () => columns.map((column) => column.header),
    [columns],
  );
  // Purchase Data's supplier band (GEN-2778): a "Supplier N" header over each
  // run of a supplier's columns, as a second header row above the columns'
  // own. Undefined on every other worksheet, which keeps one header row.
  const supplierBand = useMemo(
    () =>
      supplierBandRow(suppliers, fixedColumns, (supplier) =>
        gt('Supplier {number}', {
          number: supplier,
          context:
            'review grid header band over the Purchase Data columns of one of the three suppliers (1, 2 or 3)',
        }),
      ),
    [suppliers, fixedColumns, gt],
  );
  const nestedHeaders = useMemo(
    () => (supplierBand ? [supplierBand, colHeaders] : undefined),
    [supplierBand, colHeaders],
  );

  // A new row that isn't saved yet takes its CDB as typed: it is which
  // division (and RD location) the row is added to, so it stays editable
  // there until Save adds the row. Every other row's CDB is its record's.
  const cdbColumn = columns.findIndex(
    (column) => column.code === AS400_CDB_COLUMN,
  );
  const handleCells = (
    row: number,
    col: number,
  ): Partial<Handsontable.CellProperties> => {
    if (col !== cdbColumn || readOnly || view === 'before') return {};
    const record = hotRef.current?.hotInstance?.getSourceDataAtRow(row);
    return isGridRecord(record) && isDraftRowKey(record.__key)
      ? { readOnly: false }
      : {};
  };

  // The widths the reader has dragged, by column position, as the resize
  // plugin's initial widths. Sparse on purpose: a column with no remembered
  // width is left to size itself to its content (autoColumnSize), and only a
  // dragged one is pinned. The grid remounts whenever its rows are refetched
  // (a division, page or view switch shows the skeleton first), so this is
  // what carries a drag across those — the plugin's own memory dies with the
  // instance.
  const manualColumnWidths = useMemo(() => {
    const widths: number[] = [];
    columns.forEach((column, index) => {
      const width = columnWidths.get(columnDataKey(column, index));
      if (width !== undefined) widths[index] = width;
    });
    return widths;
  }, [columns, columnWidths]);

  // A finished drag (or a double-click autosize) on a column: remember it
  // under the column's key. The visual index is the column's position — the
  // grid never reorders or hides columns.
  const handleAfterColumnResize = (newSize: number, column: number) => {
    const key = columnSettings[column]?.data;
    if (key !== undefined) onColumnResized(key, newSize);
  };

  // Draw each header's sort chevron and note marker, and fix the doubled
  // divider in the frozen-columns header overlay: with row headers off,
  // Handsontable leaves a stray inline-start border on fixed columns after
  // the first, stacking against the previous column's border. Zero it on
  // those header cells (Handsontable's own reset is gated behind row headers).
  const handleAfterGetColHeader = (
    colIndex: number,
    th: HTMLTableCellElement,
    headerLevel?: number,
  ) => {
    // With the supplier band on, Handsontable draws two header rows and runs
    // this for each; level 0 is the band (see paintSupplierBandHeader).
    if (nestedHeaders !== undefined && headerLevel === 0) {
      paintSupplierBandHeader(th, suppliers, colIndex);
      headerNotes.markHeader(colIndex, th, false);
      return;
    }
    paintSupplierHeader(th, suppliers, colIndex, false);
    renderSortToggle(
      th,
      colIndex,
      sorting ? sortToggleStateFor(sortKeys, sorted, colIndex) : undefined,
      gt('Sort by {column}', {
        column: columns[colIndex]?.header.replace(/\s+/g, ' ').trim() ?? '',
        context:
          'aria label of the chevron in a review grid column header that sorts by that column',
      }),
    );
    headerNotes.markHeader(colIndex, th);
    if (
      colIndex > 0 &&
      colIndex < fixedColumns &&
      th.closest('.ht_clone_top_inline_start_corner')
    ) {
      th.style.borderInlineStartWidth = '0';
    }
  };

  // The supplier band labels' sticky inset (see syncSupplierBandInset):
  // measured once per full render, never from the per-header hook above.
  const handleAfterRender = () => {
    if (nestedHeaders !== undefined) syncSupplierBandInset(wrapperRef.current);
  };

  // One changed cell → its pending edit, resolved to its row via the grid
  // record's `__key`. Synthetic (`__`-prefixed) columns are read-only metadata,
  // but for an unsaved new row's CDB (see handleCells).
  const recordCellChange = (
    instance: Handsontable,
    rowIndex: number,
    prop: unknown,
    newValue: unknown,
  ) => {
    const record = instance.getSourceDataAtRow(
      instance.toPhysicalRow(rowIndex),
    );
    if (!isGridRecord(record) || typeof prop !== 'string') return;
    const draftCdb = prop === AS400_CDB_COLUMN && isDraftRowKey(record.__key);
    if (prop.startsWith('__') && !draftCdb) return;
    onCellEdited(record.__key, prop, asCellText(newValue));
  };

  /** "1 cell was" / "3 cells were", for a rejection toast. */
  const cellsWere = (count: number) =>
    `${count} ${count === 1 ? 'cell was' : 'cells were'}`;

  /** Say which cells the ASCII fold left unchanged, and why. */
  const announceNonAscii = (nonAscii: NonAsciiCellRejection[]) => {
    if (nonAscii.length === 0) return;
    const names = [...new Set(nonAscii.map((cell) => cell.editKey))].map(
      columnName,
    );
    const characters = [
      ...new Set(nonAscii.flatMap((cell) => cell.characters)),
    ];
    showToast({
      title: 'Unsupported characters',
      message: `${cellsWere(nonAscii.length)} left unchanged — the AS/400 accepts ASCII only, so ${characters.join(' ')} can't be uploaded in ${names.join(', ')}.`,
      color: 'warning',
    });
  };

  // Cells are typed in caps and in the ASCII the AS/400 accepts: rewrite and
  // vet the incoming values before they land, so the grid shows what will
  // actually be saved. See rewriteGridCellChanges.
  // Rules a value can break on its way to a valid one (a character limit) are
  // not enforced here: the value lands, and handleAfterChange flags it.
  // A reset writes the AS/400's own values back as they were, so it skips all
  // this — all but the fills that follow a reset (see below).
  //
  // Typing with several cells selected writes to all of them. The widening
  // happens here, before anything lands: the extra changes then go through
  // the same rewrites above and the same afterChange staging as the typed
  // cell — one batch, so the grid shows them and Save sees them exactly as if
  // each had been typed. `afterSetDataAtCell` would be too late for that
  // (it runs after this hook, so its copies would skip the rewrites), and
  // writing from `afterChange` would be a second batch. Enter and Tab still
  // move as before: the editor's own commit and navigation are untouched.
  //
  // Auto-fills (sheetRules/, e.g. a flag that must be Y once its field
  // changes) join the batch last, for the same reason: they read the values
  // as the rewrites left them, and land, tint and stage with the edit that
  // called for them.
  const handleBeforeChange = (
    changes: Array<Handsontable.CellChange | null>,
    source: string,
  ) => {
    const instance = hotRef.current?.hotInstance;
    if (readOnly || source === 'loadData' || source === 'updateData') {
      return;
    }
    const reset = source === RESET_SOURCE;
    if (!reset) {
      if (source === 'edit' && instance) {
        expandEditToSelection(instance, changes, blankMarkerFor);
      }
      // A pasted or typed price may come from a spreadsheet in either locale
      // (`1 234,50 $`); rewrite it to the plain decimal the AS/400 takes.
      announceNonAscii(rewriteGridCellChanges(changes));
      correctSlashCountCellChanges(changes, blankMarkerFor);
    }
    // A reset runs only the fills that follow one: a change flag goes back
    // with the value it was filled for.
    if (instance) {
      autoFillGridChanges(
        changes,
        autoFills,
        (row) => recordAt(instance, row),
        editableFields,
        { reset },
      );
    }
  };

  // Fold typed/pasted/cleared cells into the pending-edit map, then check what
  // just landed against the cell rules: this is the moment the author leaves
  // the cell (or a paste lands), so a broken rule is said then, in a popup —
  // the outline alone is easy to miss while typing with eyes elsewhere — unless
  // the editor's note is saying it already (see violationsToAnnounce).
  // `loadData` fires when the data array itself is swapped — nothing was
  // typed.
  const handleAfterChange = (
    changes: Handsontable.CellChange[] | null,
    source: string,
  ) => {
    const instance = hotRef.current?.hotInstance;
    if (readOnly || !changes || source === 'loadData' || !instance) return;
    for (const [rowIndex, prop, , newValue] of changes) {
      recordCellChange(instance, rowIndex, prop, newValue);
    }
    const violations = violationsToAnnounce(
      instance,
      changes,
      rules,
      source,
      editorCheck.noteShown(),
    );
    if (violations.length === 0) return;
    showToast({
      ...leftCellNotice(gt, violations, columnName),
      color: 'warning',
    });
  };

  // Delete clears marker columns out to their AS/400 marker, Backspace still
  // empties cells — see blankOutOnDelete for why the two now differ.
  const handleBeforeKeyDown = (event: KeyboardEvent) => {
    // While a cell's comments are open their text box has the keyboard. The
    // grid hears every keydown on the page (and re-listens as its context
    // menu closes), so it is told to leave these alone — all but Escape:
    // stopping an event also stops it bubbling, and the popover closes on an
    // Escape that reaches the document (focus is there once a new thread's
    // box has gone). The grid does nothing with Escape outside an editor.
    if (cellComments.isOpen()) {
      if (event.key !== 'Escape') {
        Handsontable.dom.stopImmediatePropagation(event);
      }
      return;
    }
    const instance = hotRef.current?.hotInstance;
    if (readOnly || !instance) return;
    blankOutOnDelete(instance, event, blankMarkerFor);
  };

  // Handsontable gets one set of hook functions for the grid's lifetime,
  // each forwarding to this render's handler above. The handlers close over
  // the current props and state, so they are new every render; passing them
  // straight through would make the grid element below new every render too
  // (see hotTable). The forwarders read the latest handlers from a ref that
  // is refreshed in a layout effect: before the wrapper's own effect, so a
  // redraw it triggers for a changed setting already sees this render's
  // handlers; Handsontable's event-driven calls come later still.
  const gridHandlers = {
    beforeChange: handleBeforeChange,
    afterChange: handleAfterChange,
    beforeKeyDown: handleBeforeKeyDown,
    afterGetColHeader: handleAfterGetColHeader,
    afterRender: handleAfterRender,
    beforeOnCellMouseDown: handleBeforeOnCellMouseDown,
    afterColumnResize: handleAfterColumnResize,
    cells: handleCells,
  };
  const latestGridHandlers = useRef(gridHandlers);
  useLayoutEffect(() => {
    latestGridHandlers.current = gridHandlers;
  });
  const gridHooks = useMemo(
    () => ({
      beforeChange: (
        changes: Array<Handsontable.CellChange | null>,
        source: string,
      ) => latestGridHandlers.current.beforeChange(changes, source),
      afterChange: (
        changes: Handsontable.CellChange[] | null,
        source: string,
      ) => latestGridHandlers.current.afterChange(changes, source),
      beforeKeyDown: (event: KeyboardEvent) =>
        latestGridHandlers.current.beforeKeyDown(event),
      afterGetColHeader: (
        colIndex: number,
        th: HTMLTableCellElement,
        headerLevel: number,
      ) =>
        latestGridHandlers.current.afterGetColHeader(colIndex, th, headerLevel),
      afterRender: () => latestGridHandlers.current.afterRender(),
      beforeOnCellMouseDown: (
        ...args: Parameters<
          NonNullable<Handsontable.GridSettings['beforeOnCellMouseDown']>
        >
      ) => latestGridHandlers.current.beforeOnCellMouseDown(...args),
      afterColumnResize: (newSize: number, column: number) =>
        latestGridHandlers.current.afterColumnResize(newSize, column),
      cells: (row: number, col: number) =>
        latestGridHandlers.current.cells(row, col),
    }),
    [],
  );

  // The grid element itself, rebuilt only when one of its settings changes.
  // The React wrapper calls `updateSettings` on every render it receives,
  // and Handsontable answers that with a full redraw (and `updateData` for
  // the data array): with the element rebuilt on each keystroke, every cell
  // commit redrew the page twice over. With the hooks stable, a pending
  // edit, a toast or a parent re-render changes none of these inputs, and
  // React skips the wrapper for the same element.
  const showRowHeaders = rowHiding !== undefined;
  const hotTable = useMemo(
    () => (
      <HotTable
        // Handsontable keeps a setting a later update leaves out, so moving
        // between a worksheet with the supplier band and one without remounts
        // the grid rather than leave the band's header row behind.
        key={nestedHeaders ? 'supplier-band' : 'single-header'}
        ref={hotRef}
        data={gridState.records}
        columns={columnSettings}
        cells={gridHooks.cells}
        colHeaders={colHeaders}
        {...(nestedHeaders ? { nestedHeaders } : {})}
        // The row gutter is where whole rows are selected to hide them, and
        // where the hidden-row indicators sit.
        rowHeaders={showRowHeaders}
        width="100%"
        height={gridHeight}
        fixedColumnsStart={fixedColumns}
        licenseKey="non-commercial-and-evaluation"
        beforeChange={gridHooks.beforeChange}
        afterChange={gridHooks.afterChange}
        beforeKeyDown={gridHooks.beforeKeyDown}
        afterGetColHeader={gridHooks.afterGetColHeader}
        afterRender={gridHooks.afterRender}
        beforeOnCellMouseDown={gridHooks.beforeOnCellMouseDown}
        afterColumnResize={gridHooks.afterColumnResize}
        outsideClickDeselects={false}
        contextMenu={contextMenu}
        // Columns size themselves to their content (headers included) and a
        // cell reads on one line, spreadsheet-style; a dragged width overrides
        // the measured one and is remembered across remounts (see above).
        manualColumnResize={manualColumnWidths}
        autoColumnSize
        wordWrap={false}
        autoRowSize={false}
        rowHeights={GRID_ROW_HEIGHT}
        enterBeginsEditing
        enterMoves={{ row: 1, col: 0 }}
        tabMoves={{ row: 0, col: 1 }}
      />
    ),
    [
      gridState.records,
      columnSettings,
      gridHooks,
      colHeaders,
      nestedHeaders,
      showRowHeaders,
      gridHeight,
      fixedColumns,
      contextMenu,
      manualColumnWidths,
    ],
  );

  return (
    <>
      <div
        ref={wrapperRef}
        className={cn(
          'rounded-md border overflow-hidden',
          // Cell text is monospaced so item numbers, CDB keys and part numbers
          // line up column by column; the open editor matches so a value doesn't
          // shift while it's typed. Headers keep the UI font.
          '[&_td]:font-mono [&_.handsontableInput]:font-mono',
          // Caps while typing, not just once the cell commits: the open cell
          // editor is a textarea Handsontable appends inside the grid root, so
          // casing it here previews what upperCaseCellChanges will store.
          !readOnly && '[&_.handsontableInput]:uppercase',
          FILL_PREVIEW_CLASSES,
          SORT_TOGGLE_HOST_CLASSES,
        )}
        onClick={(event) => {
          handleSortToggleClick(event);
          cellComments.handleClick(event);
        }}
        onMouseMove={(event) => {
          headerNotes.handleMouseMove(event);
          cellComments.handleMouseMove(event);
        }}
        onMouseLeave={() => {
          headerNotes.clear();
          cellComments.clear();
        }}
        onScroll={() => {
          headerNotes.clear();
          cellComments.clear();
        }}
        onInput={editorCheck.handleInput}
        onBlur={editorCheck.handleBlur}
      >
        {headerNotes.tooltip}
        {editorCheck.note}
        {hotTable}
      </div>
      {/* Outside the wrapper, whose handlers would otherwise hear the
          popover's typing and clicks (React events bubble through portals). */}
      {cellComments.overlays}
    </>
  );
}

/** Unsaved-edit count plus the Discard/Save actions, shown only while dirty. */
function UnsavedEditsActions({
  pendingCellCount,
  isSaving,
  onDiscard,
  onSave,
}: {
  pendingCellCount: number;
  isSaving: boolean;
  onDiscard: () => void;
  onSave: () => void;
}) {
  if (pendingCellCount === 0) return null;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">
        <T context="count of unsaved spreadsheet cell edits">
          Unsaved edits: <Var>{pendingCellCount}</Var>
        </T>
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={onDiscard}
        disabled={isSaving}
      >
        <T context="discard unsaved spreadsheet edits">Discard</T>
      </Button>
      <Button size="sm" onClick={onSave} disabled={isSaving}>
        <T context="save spreadsheet edits as staged changes">Save edits</T>
      </Button>
    </div>
  );
}

/** The section's table area: skeleton while loading, empty state, or the grid. */
/**
 * Escape leaves the review's focus mode, unless the grid has a cell editor open
 * (Escape then cancels the edit) or a dialog is up (Escape closes the dialog).
 */
function useEscapeLeavesFocus(
  focus: ReviewFocus | null,
  gridRef: RefObject<As400GridFocusHandle | null>,
): void {
  const focused = focus?.focused ?? false;
  const setFocused = focus?.setFocused;
  useEffect(() => {
    if (!focused || !setFocused) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (gridRef.current?.isEditing()) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) {
        return;
      }
      setFocused(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [focused, setFocused, gridRef]);
}

function FocusModeToggle({
  focused,
  onToggle,
}: {
  focused: boolean;
  onToggle: () => void;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      aria-pressed={focused}
      onClick={onToggle}
    >
      {focused ? (
        <>
          <ArrowsInIcon className="h-4 w-4" />
          <T context="button, leave the full-screen spreadsheet view">
            Exit Focus
          </T>
        </>
      ) : (
        <>
          <ArrowsOutIcon className="h-4 w-4" />
          <T context="button, expand the spreadsheet to fill the screen">
            Focus
          </T>
        </>
      )}
    </Button>
  );
}

/** What the grid takes, shared by {@link EditableSectionBody} which wraps it. */
interface GridBodyProps {
  columns: readonly As400ExportColumn[];
  pending: PendingEditsByRowKey;
  view: StagedView;
  onCellEdited: (rowKey: string, code: string, typed: string | null) => void;
  dataVersion: number;
  readOnly: boolean;
  fixedColumns: number;
  columnWidths: ColumnWidthsByKey;
  onColumnResized: (dataKey: string, width: number) => void;
  newRowActions?: NewRowGridActions;
  focusRef?: Ref<As400GridFocusHandle>;
  /** The review's column sort; omitted = headers don't sort. */
  sorting?: GridSorting;
  /** The review's hidden rows; omitted = no row gutter, rows can't be hidden. */
  rowHiding?: GridRowHiding;
  /** Hidden worksheet columns; omitted = columns can't be hidden. */
  columnHiding?: GridColumnHiding;
  takenItemNumbers?: ReadonlySet<string>;
  lookups?: CellRowLookups;
  /** The review's cell comments; omitted = no comments. */
  comments?: GridCellComments;
}

function EditableSectionBody({
  rows,
  isLoading,
  ...gridProps
}: GridBodyProps & {
  rows: As400ExportRow[] | undefined;
  isLoading: boolean;
}) {
  const { columns } = gridProps;
  if (isLoading || rows === undefined) {
    return (
      <As400ExportTableSkeleton columnCount={Math.min(columns.length, 8)} />
    );
  }
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        <T context="empty state for the flattened AS/400 view">
          No records found.
        </T>
      </p>
    );
  }
  return <As400EditableExportGrid {...gridProps} rows={rows} />;
}

/**
 * The flattened AS/400 grid, fed with an already-fetched page of rows and its
 * column set. When `editable` it's inline-editable: a Before/After toggle swaps
 * the table between live values (Before, read-only) and values with staged +
 * unsaved edits applied (After, editable); edits are held locally per `scopeKey`
 * and committed via `onSave`. When not editable it's fully read-only (live
 * values, no toggle, no save) — cells stay selectable/copyable either way.
 */
export function As400FlatGridSection({
  columns,
  scopeKey,
  editable,
  rows,
  isLoading,
  error,
  paginationConfig,
  onSave,
  isSaving = false,
  fixedColumns = 0,
  toolbarExtra,
  newRowActions,
  focusRef,
  pendingRef,
  onDiscard,
  columnWidths,
  onColumnResized,
  sorting,
  rowHiding,
  columnHiding,
  errorNavigation,
  itemAddRows,
  itemAddNewRows,
  itemAddScopePrefix,
  itemAddDivisions,
  itemAddReady = true,
  itemAddError = null,
  itemSheetRows,
  itemSheetNewRows,
  itemSheetScopePrefix,
  itemSheetReady = true,
  itemSheetError = null,
  stockUomLookupRequired = false,
  comments,
}: {
  columns: readonly As400ExportColumn[];
  scopeKey: string;
  editable: boolean;
  rows: As400ExportRow[] | undefined;
  isLoading: boolean;
  error: Error | null;
  paginationConfig: PaginationConfig;
  onSave?: (edits: PendingRowEdit[]) => Promise<void>;
  isSaving?: boolean;
  // Number of leading columns to freeze while the rest scroll horizontally.
  fixedColumns?: number;
  /** Extra controls rendered in the editable toolbar row (e.g. Add row). */
  toolbarExtra?: ReactNode;
  /** Row-level actions for authored new rows (context menu). */
  newRowActions?: NewRowGridActions;
  /** Receives the grid's focus handle (see {@link As400GridFocusHandle}). */
  focusRef?: RefObject<As400GridFocusHandle | null>;
  /** Receives the pending-edit handle ({@link As400GridPendingActions}). */
  pendingRef?: Ref<As400GridPendingActions>;
  /** Runs when the user discards — the owner drops its draft rows too. */
  onDiscard?: () => void;
  /** Dragged widths, held by the page so they outlive this grid (see useColumnWidths). */
  columnWidths: ColumnWidthsByKey;
  onColumnResized: (dataKey: string, width: number) => void;
  /** The review's column sort (worksheets only). */
  sorting?: GridSorting;
  /** The review's hidden rows (worksheets only). */
  rowHiding?: GridRowHiding;
  /** Hidden worksheet columns (worksheets only). */
  columnHiding?: GridColumnHiding;
  /** The review's worksheets, for the error summary (worksheets only). */
  errorNavigation?: ReviewErrorNavigation;
  itemAddRows?: As400ExportRow[];
  itemAddNewRows?: ChangeSetNewRow[];
  itemAddScopePrefix?: string;
  /** The review's divisions, to keep Item Add presence within one division. */
  itemAddDivisions?: readonly DivisionOption[];
  itemAddReady?: boolean;
  itemAddError?: Error | null;
  itemSheetRows?: As400ExportRow[];
  itemSheetNewRows?: { id: string; values: Record<string, string | null> }[];
  itemSheetScopePrefix?: string;
  itemSheetReady?: boolean;
  itemSheetError?: Error | null;
  stockUomLookupRequired?: boolean;
  /**
   * The review's cell comments, and how to find a comment's row for a jump
   * to it (worksheets only; absent while the grid can't look a row up yet,
   * so a jump waits for it).
   */
  comments?: GridCellComments & { locate?: LocateCommentRow };
}) {
  const { showToast } = useToast();
  const gt = useGT();
  const page = paginationConfig.pagination.currentPage;

  // Unsaved edits, kept per scope so they survive paging and group switches;
  // saving writes the active scope's edits.
  const [pendingByScope, setPendingByScope] = useState<
    Map<string, PendingEditsByRowKey>
  >(new Map());
  const pending =
    pendingByScope.get(scopeKey) ?? new Map<string, PendingRowEdit>();
  const pendingCellCount = [...pending.values()].reduce(
    (count, edit) => count + Object.keys(edit.values).length,
    0,
  );
  const addItemNumbers = pendingAddItemNumbers(pendingByScope);
  const {
    data: resolvedItemNumbers,
    isPending: itemNumbersPending,
    isError: itemNumbersError,
  } = useResolveItemNumbers(addItemNumbers);
  const takenItemNumbers = takenItemNumbersOf(resolvedItemNumbers);
  const { lookups, gridLookups, itemSheetGate } = useAs400CellRowLookups({
    pendingByScope,
    itemAddReady,
    itemAddRows,
    itemAddNewRows,
    itemAddScopePrefix,
    itemAddDivisions,
    itemSheetReady,
    itemSheetRows,
    itemSheetNewRows,
    itemSheetScopePrefix,
    itemSheetError,
    stockUomLookupRequired,
  });

  // Bumped when the pending map changes outside the grid's own typing flow
  // (discard, save) so the grid rebuilds its data from scratch.
  const [dataVersion, setDataVersion] = useState(0);

  const [view, setView] = useState<StagedView>('after');

  const ownFocusRef = useRef<As400GridFocusHandle>(null);
  const gridFocusRef = focusRef ?? ownFocusRef;
  const focus = useReviewFocus();
  useEscapeLeavesFocus(focus, gridFocusRef);

  useImperativeHandle(pendingRef, () => ({
    stageRowValues(targetScopeKey, rowKey, values) {
      setPendingByScope((prev) => {
        const next = new Map(prev);
        const scoped = new Map(prev.get(targetScopeKey) ?? []);
        scoped.set(rowKey, {
          rowKey,
          sources: [],
          values,
          // A draft row has no live cells, so every baseline is empty.
          originalValues: Object.fromEntries(
            Object.keys(values).map((code) => [code, null]),
          ),
        });
        next.set(targetScopeKey, scoped);
        return next;
      });
    },
    hasRowEdits(targetScopeKey, rowKey) {
      return pendingByScope.get(targetScopeKey)?.has(rowKey) ?? false;
    },
    clearRowEdits(targetScopeKey, rowKey) {
      setPendingByScope((prev) => {
        const scoped = prev.get(targetScopeKey);
        if (!scoped?.has(rowKey)) return prev;
        const next = new Map(prev);
        const nextScoped = new Map(scoped);
        nextScoped.delete(rowKey);
        if (nextScoped.size === 0) next.delete(targetScopeKey);
        else next.set(targetScopeKey, nextScoped);
        return next;
      });
    },
  }));

  // The row each unsaved edit was made against and where it sat, so Save can
  // check an edit's rules against the row's other cells, and the error list
  // can find it, after the author has paged away from it. Only ever read
  // through the pending edits, so a stale entry is inert.
  const [editedRows, setEditedRows] = useState<Map<string, EditedRow>>(
    new Map(),
  );
  // Where a row is now, if it is on this page; else where it was edited.
  const editedRow = useCallback(
    (rowKey: string): EditedRow | undefined => {
      const index = rows?.findIndex((row) => row.key === rowKey) ?? -1;
      const row = rows?.[index];
      return row ? { row, page, index } : editedRows.get(rowKey);
    },
    [rows, page, editedRows],
  );

  const handleCellEdited = (
    rowKey: string,
    code: string,
    typed: string | null,
  ) => {
    const located = editedRow(rowKey);
    if (!located) return;
    const { row } = located;
    setEditedRows((prev) =>
      prev.get(rowKey)?.row === row ? prev : new Map(prev).set(rowKey, located),
    );
    setPendingByScope((prev) => {
      const next = new Map(prev);
      const scoped = applyCellEdit(
        prev.get(scopeKey) ?? new Map<string, PendingRowEdit>(),
        row,
        code,
        typed,
      );
      if (scoped.size === 0) next.delete(scopeKey);
      else next.set(scopeKey, scoped);
      return next;
    });
  };

  const clearActiveScope = () => {
    setPendingByScope((prev) => {
      const next = new Map(prev);
      next.delete(scopeKey);
      return next;
    });
    setDataVersion((version) => version + 1);
  };

  // Drop exactly the snapshot a save submitted — not the whole scope. Edits
  // made while the save was in flight (typed cells, a fresh draft row) have
  // replaced their row's entry in the map (applyCellEdit and stageRowValues
  // write new objects), so reference equality tells the two apart.
  const clearSavedEdits = (submitted: PendingEditsByRowKey) => {
    setPendingByScope((prev) => {
      const scoped = prev.get(scopeKey);
      if (!scoped) return prev;
      const nextScoped = new Map(scoped);
      for (const [rowKey, edit] of submitted) {
        if (nextScoped.get(rowKey) === edit) nextScoped.delete(rowKey);
      }
      const next = new Map(prev);
      if (nextScoped.size === 0) next.delete(scopeKey);
      else next.set(scopeKey, nextScoped);
      return next;
    });
    setDataVersion((version) => version + 1);
  };

  // Save stages nothing while an unsaved edit breaks a cell rule: an error
  // describes a value the AS/400 can't take, so a partial save would only
  // split one review's edits across two saves. Warnings don't stop it: they
  // ask for a second look, not a fix. The popup names the
  // first problem; the grid outlines all of them. A cell still being typed in
  // is already committed by the time Save's click runs: with
  // `outsideClickDeselects` off, Handsontable finishes editing on the
  // mousedown outside the grid, so the value is in `pending` and checked too.
  const rules = as400CellRules(columns, { takenItemNumbers });
  const handleSave = async () => {
    if (
      !onSave ||
      pending.size === 0 ||
      !itemAddReady ||
      itemSheetGate.blockSave
    ) {
      return;
    }
    if (
      itemNumberLookupBlocksSave(addItemNumbers, {
        isPending: itemNumbersPending,
        isError: itemNumbersError,
      })
    ) {
      showToast({
        ...itemNumberLookupNotice(gt, itemNumbersError),
        color: 'warning',
      });
      return;
    }
    const blocking = pendingViolations(
      pending,
      rules,
      (rowKey) => editedRow(rowKey)?.row,
      lookups,
    );
    if (blocking.length > 0) {
      showToast({
        ...saveBlockedNotice(gt, blocking, columnNamer(columns)),
        color: 'warning',
      });
      return;
    }
    const submitted = pending;
    try {
      await onSave([...submitted.values()]);
      clearSavedEdits(submitted);
      showToast({
        title: 'Edits staged',
        message: 'Your edits were saved to the change set.',
        color: 'success',
      });
    } catch {
      // The staging hook surfaces the error toast; keep the edits pending.
    }
  };

  const goToCell = useWorksheetCellJump({
    onOpen: errorNavigation?.onOpen,
    group: errorNavigation?.scopes.find((scope) => scope.scopeKey === scopeKey)
      ?.group,
    page,
    isLoaded: !isLoading && rows !== undefined,
  });
  const errorSummary = useReviewErrorNavigation({
    navigation: errorNavigation,
    pendingByScope,
    editedRow,
    takenItemNumbers,
    goToCell,
    grid: gridFocusRef,
    lookups,
  });
  useReviewCommentJump({
    locate: comments?.locate,
    goToCell,
    grid: gridFocusRef,
  });

  if (error || itemAddError || itemSheetGate.displayError) {
    return (
      <ErrorMessage
        error={error ?? itemAddError ?? itemSheetGate.displayError}
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* View toggle (Before ↔ After) + unsaved-edit actions — editable scope
          only; the read-only item-catalog view has neither. */}
      {editable && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <BeforeAfterToggle value={view} onChange={setView} />
            {toolbarExtra}
            {focus && (
              <FocusModeToggle
                focused={focus.focused}
                onToggle={() => focus.setFocused(!focus.focused)}
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <ReviewErrorsNavigator
              errors={errorSummary.errors}
              activeIndex={errorSummary.activeIndex}
              describe={errorSummary.describe}
              onJump={errorSummary.jump}
            />
            <UnsavedEditsActions
              pendingCellCount={pendingCellCount}
              isSaving={isSaving || !itemAddReady || itemSheetGate.blockSave}
              onDiscard={() => {
                onDiscard?.();
                clearActiveScope();
              }}
              onSave={handleSave}
            />
          </div>
        </div>
      )}

      <EditableSectionBody
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        pending={pending}
        view={editable ? view : 'after'}
        onCellEdited={handleCellEdited}
        dataVersion={dataVersion}
        readOnly={!editable}
        fixedColumns={fixedColumns}
        columnWidths={columnWidths}
        onColumnResized={onColumnResized}
        newRowActions={newRowActions}
        focusRef={gridFocusRef}
        sorting={sorting}
        rowHiding={rowHiding}
        columnHiding={columnHiding}
        takenItemNumbers={takenItemNumbers}
        lookups={gridLookups}
        comments={comments}
      />

      <TablePagination
        pagination={paginationConfig.pagination}
        onPageSizeChange={paginationConfig.onPageSizeChange}
        onPreviousPage={paginationConfig.onPreviousPage}
        onNextPage={paginationConfig.onNextPage}
        onFirstPage={paginationConfig.onFirstPage}
        onLastPage={paginationConfig.onLastPage}
        showRowsPerPage={paginationConfig.showRowsPerPage}
        showPageInfo={paginationConfig.showPageInfo}
        showPageNumbers={paginationConfig.showPageNumbers}
      />
    </div>
  );
}

/** The divisions, filters and locations a worksheet's rows are queried with. */
export interface WorksheetQuery {
  dataSourceIds: string[];
  advancedFilters: CatalogAdvancedFilter[];
  locations: string[];
}

function PriceCurrentRecordsNote({ group }: { group: As400ExportGroup }) {
  if (group !== 'PRICE') return null;
  return (
    <p className="text-xs text-muted-foreground">
      <T context="Price worksheet helper: review grid shows only current AS/400 price records (PRICE RECORD ID C)">
        Showing current price records only (PRICE RECORD ID C).
      </T>
    </p>
  );
}

/**
 * One export worksheet on the review page: fetches one page of the
 * division-scoped flattened worksheet (narrowed by `advancedFilters`, which the
 * ingest server evaluates) and renders it as the inline-editable
 * {@link As400FlatGridSection}, staging edits via the worksheet path (group +
 * division resolve each column to its owning ingest table).
 */
export function As400ExportEditableSection({
  group,
  dataSourceIds,
  divisionsLoading = false,
  changeSetId,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  advancedFilters,
  locations,
  columnWidths,
  onColumnResized,
  layout,
  onDivisionsAuthored,
  onOpenWorksheet,
  worksheetQuery,
}: {
  group: As400ExportGroup;
  dataSourceIds: string[];
  /**
   * Which divisions have records on this worksheet is still loading, so
   * `dataSourceIds` is provisional: the grid waits rather than show rows it
   * is about to swap (and a jump to a cell lands once they are final).
   */
  divisionsLoading?: boolean;
  changeSetId: string;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  advancedFilters: CatalogAdvancedFilter[];
  /** The Location filter's codes (or whole CDB keys); empty = every location. */
  locations?: string[];
  columnWidths: ColumnWidthsByKey;
  onColumnResized: (dataKey: string, width: number) => void;
  /** The review-wide sort, hidden rows and hidden columns. */
  layout: ReviewGridLayout;
  /**
   * Rows were just saved for these divisions. Add row offers every division,
   * not only the ones in view, so the owner widens the view to keep the saved
   * rows on screen — the grid only lists the new rows of divisions it queries.
   */
  onDivisionsAuthored?: (dataSourceIds: string[]) => void;
  /** Show another worksheet at a page — where an error in the list sits. */
  onOpenWorksheet: (group: As400ExportGroup, page: number) => void;
  /**
   * What a worksheet is shown under: this one's filters and divisions, or
   * what opening another (`onOpenWorksheet`) would show it under.
   */
  worksheetQuery: (group: As400ExportGroup) => Promise<WorksheetQuery>;
}) {
  const gt = useGT();
  const m = useMessages();
  const { showToast } = useToast();
  const {
    sort,
    onSortChange,
    hiddenRows,
    onHiddenRowsChange,
    hiddenColumns,
    onHiddenColumnsChange,
  } = layout;
  const gridFocusRef = useRef<As400GridFocusHandle>(null);
  const gridPendingRef = useRef<As400GridPendingActions>(null);
  // Held stable per group: the grid's column settings are derived from this
  // array, and a fresh one each render would hand Handsontable new settings on
  // every keystroke (the group is the only thing that changes them).
  const columns = worksheetGridColumns(group);
  const fixedColumns = worksheetFixedColumnCount(columns, group);
  const hiddenHere = useMemo(
    () =>
      new Set(
        hiddenColumns
          .filter((hidden) => hidden.group === group)
          .map((hidden) => hidden.column),
      ),
    [hiddenColumns, group],
  );
  const hideColumns = (columnKeys: string[]) =>
    onHiddenColumnsChange([
      ...hiddenColumns,
      ...columnKeys
        .filter((column) => !hiddenHere.has(column))
        .map((column) => ({ group, column })),
    ]);
  const showColumns = () =>
    onHiddenColumnsChange(
      hiddenColumns.filter((hidden) => hidden.group !== group),
    );
  const reviewDivisions = useDivisionOptions();
  const reviewDivisionIds = reviewDivisions.map((division) => division.id);
  const itemAddIndex = useAs400ItemAddIndex(reviewDivisionIds, changeSetId);
  const itemSheetIndex = useAs400ItemSheetIndex(reviewDivisionIds, changeSetId);
  const { data, isLoading, error } = useAs400ExportView(
    group,
    dataSourceIds,
    page,
    pageSize,
    advancedFilters,
    changeSetId,
    locations,
    sort,
  );
  const commentThreads = useChangeSetCommentThreads(changeSetId).data?.threads;
  const threadsByCell = commentThreads
    ? indexThreadsByCell(commentThreads)
    : NO_CELL_THREADS;
  // A comment's row, found under the filters its worksheet is shown under. A
  // staged new row is pinned ahead of page 1 on every worksheet that lists it.
  const locateCommentRow: LocateCommentRow = async (target) => {
    const { row } = target;
    if (row.kind === 'new') {
      return { page: 1, rowKey: newRowKey(row.stagedNewRowId) };
    }
    const query = await worksheetQuery(target.group);
    // Every division picked is empty on that worksheet: the grid can't show
    // the row ("Cell not shown"), and the lookup needs at least one division.
    if (query.dataSourceIds.length === 0) return null;
    const { location } = await locateAs400ExportRow({
      ...query,
      group: target.group,
      changeSetId,
      pageSize,
      sort: sort ?? undefined,
      row: { dataSourceLinkId: row.dataSourceLinkId, dataId: row.dataId },
    });
    return location;
  };
  const addRow = useAddChangeSetNewRow();
  const stageEdits = useStageDataLinkEdits();
  const updateNewRows = useUpdateChangeSetNewRows();
  const deleteNewRow = useDeleteChangeSetNewRow();

  const scopeKey = worksheetScopeKey(changeSetId, group, dataSourceIds);
  // Every worksheet of the review under the same divisions, in tab order —
  // the scopes the error summary lists unsaved edits from.
  const errorScopes = useMemo(
    () => worksheetErrorScopes(changeSetId, dataSourceIds),
    [changeSetId, dataSourceIds],
  );
  const errorNavigation: ReviewErrorNavigation = {
    scopes: errorScopes,
    onOpen: (worksheet, targetPage) =>
      worksheet === group
        ? onPageChange(targetPage)
        : onOpenWorksheet(worksheet, targetPage),
  };

  // An authored row has no ingest record to read a CDB key off, so its key is
  // the division it was authored for — whose code is the company and division
  // letters of a CDB — followed by its location when it was added at one of
  // RD's (`RDA`). Null while the division list loads.
  const { dataSources } = useDivisionSelection();
  const cdbOf = (row: {
    dataSourceId: string | null;
    branch: string | null;
  }) => {
    const division = dataSources.find(
      (dataSource) => dataSource.id === row.dataSourceId,
    );
    return division
      ? `${as400DivisionCode(division.title)}${row.branch ?? ''}`
      : null;
  };

  // Draft new rows: authored in the Add-row dialog, held locally as pure
  // pending state (their cells live in the grid's pending-edit map via
  // `gridPendingRef`) until Save adds them to the change set. Kept per scope,
  // like the pending edits themselves. Only the division needs remembering —
  // it isn't a grid column, and it is the row's CDB.
  const [draftsByScope, setDraftsByScope] = useState<
    Map<string, DraftNewRow[]>
  >(new Map());

  // A blank new row always sits at the top of the grid: the CDB and every
  // other cell are typed straight into it, and Save adds it. It stands in
  // while the worksheet has no drafts of its own, under one key per scope, so
  // what is typed into it stays put; Add Row (or the dialog) makes it a draft
  // like any other once it has something in it. It starts under the division
  // in view when there is exactly one that adds rows without a location.
  const blankDraft = (key: string): DraftNewRow => {
    const inView =
      dataSourceIds.length === 1
        ? dataSources.find((dataSource) => dataSource.id === dataSourceIds[0])
        : undefined;
    return {
      key,
      dataSourceId:
        inView && !as400DivisionExportsPerBranch(inView.title)
          ? inView.id
          : null,
      branch: null,
    };
  };
  // A scope's drafts as a new one joins them: its own, or else its blank row
  // when that has been typed in — so what was typed there isn't lost.
  const draftsKeepingEntry = (
    prev: Map<string, DraftNewRow[]>,
    target: string,
  ): DraftNewRow[] => {
    const scoped = prev.get(target) ?? [];
    if (scoped.length > 0) return scoped;
    const entryKey = entryRowKey(target);
    return gridPendingRef.current?.hasRowEdits(target, entryKey)
      ? [blankDraft(entryKey)]
      : [];
  };
  const ownDrafts = draftsByScope.get(scopeKey) ?? [];
  const drafts =
    ownDrafts.length > 0 ? ownDrafts : [blankDraft(entryRowKey(scopeKey))];

  // Add Row: a fresh blank row on top, its CDB cell selected for typing.
  const addBlankRow = () => {
    const key = draftRowKey(crypto.randomUUID());
    setDraftsByScope((prev) =>
      new Map(prev).set(scopeKey, [
        blankDraft(key),
        ...draftsKeepingEntry(prev, scopeKey),
      ]),
    );
    requestAnimationFrame(() =>
      gridFocusRef.current?.editCell(key, AS400_CDB_COLUMN),
    );
  };

  // One draft per target the dialog picked — a division, or one of RD's
  // locations: the same item number is a distinct AS/400 record in each, so
  // each gets its own row and its own add. A row picked for several
  // worksheets is drafted on each, under that worksheet's own scope: it waits
  // there, unsaved, until the reviewer opens that worksheet, fills it in and
  // saves it.
  const addDrafts = (input: {
    groups: As400ExportGroup[];
    targets: NewRowTarget[];
    itemNumber: string;
  }) => {
    const added = input.groups.map((target) => ({
      group: target,
      scopeKey: worksheetScopeKey(changeSetId, target, dataSourceIds),
      drafts: input.targets.map((draftTarget): DraftNewRow => ({
        key: draftRowKey(crypto.randomUUID()),
        dataSourceId: draftTarget.dataSourceId,
        branch: draftTarget.branch,
      })),
    }));
    setDraftsByScope((prev) => {
      const next = new Map(prev);
      for (const sheet of added) {
        next.set(sheet.scopeKey, [
          ...draftsKeepingEntry(prev, sheet.scopeKey),
          ...sheet.drafts,
        ]);
      }
      return next;
    });
    // The item number enters the grid as a pending cell edit, so the draft
    // row shows up in "Unsaved edits" and goes through Save like typed cells.
    // A stock row's location is a cell; where the target's stocking location
    // is known it starts filled in.
    for (const sheet of added) {
      sheet.drafts.forEach((draft, index) => {
        gridPendingRef.current?.stageRowValues(
          sheet.scopeKey,
          draft.key,
          withAddRowDefaults(as400AddRowDefaults(sheet.group), {
            ...newRowLocationSeed(sheet.group, input.targets[index]),
            [AS400_EXPORT_GROUP_SEARCH_COLUMN[sheet.group]]: input.itemNumber,
          }),
        );
      });
    }
    const elsewhere = input.groups.filter((target) => target !== group);
    if (elsewhere.length > 0) {
      showToast({
        title: gt('Rows added to {count} worksheets', {
          context: 'toast title: new rows drafted on several worksheets',
          count: String(input.groups.length),
        }),
        message: gt(
          'Each worksheet keeps its new rows unsaved until you fill them in and save them there, with the same divisions selected: {worksheets}.',
          {
            context:
              'toast body after adding new rows to several worksheets, followed by the worksheet names',
            worksheets: input.groups
              .map((target) => m(AS400_EXPORT_GROUP_LABELS[target]))
              .join(', '),
          },
        ),
        color: 'success',
      });
    }
  };

  const removeDraft = (rowKey: string) => {
    setDraftsByScope((prev) => {
      const scoped = (prev.get(scopeKey) ?? []).filter(
        (draft) => draft.key !== rowKey,
      );
      const next = new Map(prev);
      if (scoped.length === 0) next.delete(scopeKey);
      else next.set(scopeKey, scoped);
      return next;
    });
    gridPendingRef.current?.clearRowEdits(scopeKey, rowKey);
  };

  const removeAllDrafts = () => {
    setDraftsByScope((prev) => {
      const next = new Map(prev);
      next.delete(scopeKey);
      return next;
    });
  };

  const paginationConfig = buildAs400PaginationConfig({
    page,
    pageSize,
    pageInfo: data?.pageInfo,
    onPageChange,
    onPageSizeChange,
  });

  // The Export tab is division-scoped: no division to query (the org has no
  // DataSources, or none selected) means the query is disabled, so show an
  // explicit empty state instead of a perpetual skeleton.
  if (dataSourceIds.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        <T context="empty state when no division/data source exists to export">
          No divisions available.
        </T>
      </p>
    );
  }

  // Authored new rows render as normal grid rows pinned ahead of the page:
  // no ingest sources, no live values — every cell they carry IS a staged
  // value, so the existing overlay path draws and tints them (After shows the
  // values; Before shows blank cells marked as changing). They are listed in
  // the same item-then-CDB order as the ingest rows, or in the review's sort
  // when it has one, so a row added on several worksheets sits in the same
  // place on each. Draft rows pin ahead
  // of those, in the order they were added: entirely empty, their cells exist
  // only as pending edits.
  //
  // Both carry their CDB as a live value rather than a staged one: it is the
  // row's identity (the division it was authored for), not a cell the review is
  // changing, so it reads the same in both views and untinted.
  const draftGridRows: As400ExportRow[] = drafts.map((draft) => ({
    key: draft.key,
    sources: [],
    values: { [AS400_CDB_COLUMN]: cdbOf(draft) },
    stagedValues: {},
  }));
  const itemColumn = AS400_EXPORT_GROUP_SEARCH_COLUMN[group];
  // The review's sort orders them too, among themselves (see sortNewRows).
  const newRowGridRows: As400ExportRow[] = sortNewRows(
    (data?.newRows ?? [])
      .map((newRow) => ({
        key: newRowKey(newRow.id),
        sources: [],
        values: { [AS400_CDB_COLUMN]: cdbOf(newRow) },
        stagedValues: newRow.values,
      }))
      .sort((a, b) =>
        compareAs400RowOrder(
          newRowOrderKey(a, itemColumn),
          newRowOrderKey(b, itemColumn),
        ),
      ),
    sort,
    group,
    columns.map(columnSortKey),
  );
  const rows = data
    ? [...draftGridRows, ...newRowGridRows, ...data.rows]
    : undefined;
  const hiddenRowCount = rows
    ? hiddenRowIndexes(rows, itemColumn, hiddenRows).length
    : 0;

  // A save routes each edited row by its key: draft rows are added to the
  // change set, saved new rows merge into their own records, and everything
  // else stages to its ingest sources. The requests are independent, not
  // atomic — deliberately: all are idempotent merges except the adds, and
  // each draft that lands is converted immediately (row + pending edits
  // dropped, the refetched `new:` row takes its place), so a partial failure
  // keeps only what actually failed pending and retrying converges.
  const handleSave = async (edits: PendingRowEdit[]) => {
    const draftEdits = edits.filter((edit) => isDraftRowKey(edit.rowKey));
    const newRowEdits = edits.filter((edit) => isNewRowKey(edit.rowKey));
    const sourceEdits = edits.filter(
      (edit) => !isDraftRowKey(edit.rowKey) && !isNewRowKey(edit.rowKey),
    );
    // Each new row goes to the division its CDB names: the one typed into
    // it, else the one it was added under. Nothing is saved while one names
    // none — the row would have nowhere to go.
    const placed = draftEdits.map((edit) => {
      const { [AS400_CDB_COLUMN]: typedCdb, ...values } = edit.values;
      const draft = drafts.find((candidate) => candidate.key === edit.rowKey);
      const division = dataSources.find(
        (dataSource) => dataSource.id === draft?.dataSourceId,
      );
      const addedAt =
        draft && division
          ? {
              dataSourceId: division.id,
              branch: draft.branch,
              cdb: `${as400DivisionCode(division.title)}${draft.branch ?? ''}`,
            }
          : null;
      const target =
        typedCdb !== undefined
          ? parseNewRowCdb(dataSources, typedCdb)
          : addedAt;
      return {
        edit,
        values: target
          ? newRowValuesAt(group, values, addedAt, target)
          : values,
        target,
        typedCdb,
      };
    });
    const unplaced = placed.filter((row) => row.target === null);
    if (unplaced.length > 0) {
      showToast({
        title: gt('Nothing saved: a new row needs a CDB', {
          context:
            'toast title: save refused because a new grid row has no valid CDB',
        }),
        message: gt(
          "Type the division's code into each new row's CDB cell, with the location for RD (RDA). Not a division you can add to: {cdbs}",
          {
            context:
              'toast body: which CDBs typed into new rows name no division; RDA is an example CDB, leave it as is',
            cdbs: unplaced
              .map(
                (row) =>
                  row.typedCdb ||
                  gt('(blank)', {
                    context: 'a CDB cell left empty, in a list of bad CDBs',
                  }),
              )
              .join(', '),
          },
        ),
        color: 'warning',
      });
      throw new Error('New rows without a division');
    }
    const authoredDivisionIds = new Set<string>();
    await Promise.all([
      ...placed.map(({ edit, values, target }) => {
        if (!target) return Promise.resolve();
        return addRow
          .mutateAsync({
            changeSetId,
            group,
            dataSourceId: target.dataSourceId,
            branch: target.branch,
            values: withAddRowDefaults(as400AddRowDefaults(group), values),
          })
          .then(() => {
            authoredDivisionIds.add(target.dataSourceId);
            removeDraft(edit.rowKey);
          });
      }),
      sourceEdits.length > 0
        ? stageEdits.mutateAsync({
            changeSetId,
            group,
            dataSourceIds,
            edits: sourceEdits.map((edit) => ({
              sources: edit.sources,
              values: edit.values,
              originalValues: edit.originalValues,
            })),
          })
        : Promise.resolve(),
      newRowEdits.length > 0
        ? updateNewRows.mutateAsync({
            changeSetId,
            edits: newRowEdits.map((edit) => ({
              id: newRowIdOf(edit.rowKey),
              values: edit.values,
            })),
          })
        : Promise.resolve(),
    ]);
    // Only once everything landed: widening the view re-scopes the grid, and
    // edits a failed request left pending must stay on screen to retry.
    if (authoredDivisionIds.size > 0) {
      onDivisionsAuthored?.([...authoredDivisionIds]);
    }
  };

  return (
    <div className="space-y-2">
      <PriceCurrentRecordsNote group={group} />
      <HiddenNewRowsNote count={data?.hiddenNewRowCount ?? 0} />
      <As400FlatGridSection
        columns={columns}
        fixedColumns={fixedColumns}
        columnWidths={columnWidths}
        onColumnResized={onColumnResized}
        scopeKey={scopeKey}
        editable
        rows={rows}
        isLoading={isLoading || divisionsLoading}
        error={error}
        paginationConfig={paginationConfig}
        onSave={handleSave}
        isSaving={
          addRow.isPending ||
          stageEdits.isPending ||
          updateNewRows.isPending ||
          deleteNewRow.isPending
        }
        focusRef={gridFocusRef}
        pendingRef={gridPendingRef}
        onDiscard={removeAllDrafts}
        sorting={{ group, sort, onSortChange }}
        rowHiding={{
          itemColumn,
          hidden: hiddenRows,
          onHiddenChange: onHiddenRowsChange,
          menuLabels: {
            hideSelected: gt('Hide selected rows', {
              context: 'grid context menu: hide the selected spreadsheet rows',
            }),
            hideItems: (itemNumbers) => hideItemsMenuLabel(gt, itemNumbers),
          },
        }}
        errorNavigation={errorNavigation}
        itemAddRows={itemAddIndex.data?.rows}
        itemAddNewRows={itemAddIndex.data?.newRows}
        itemAddReady={itemAddIndex.data !== undefined}
        itemAddError={itemAddIndex.error}
        itemAddScopePrefix={worksheetScopePrefix(changeSetId, 'ITEM_ADD')}
        itemAddDivisions={reviewDivisions}
        itemSheetRows={itemSheetIndex.data?.rows}
        itemSheetNewRows={itemSheetIndex.data?.newRows}
        itemSheetReady={itemSheetIndex.data !== undefined}
        itemSheetError={itemSheetIndex.error}
        itemSheetScopePrefix={worksheetScopePrefix(changeSetId, 'ITEM')}
        stockUomLookupRequired={group === 'PURCHASE_DATA'}
        comments={{
          changeSetId,
          group,
          threadsByCell,
          // The lookup reads the selected divisions: none until they load.
          locate: divisionsLoading ? undefined : locateCommentRow,
        }}
        columnHiding={{
          menuLabel: gt('Hide columns', {
            context: 'grid context menu: hide the selected spreadsheet columns',
          }),
          hidden: hiddenHere,
          onHide: hideColumns,
        }}
        toolbarExtra={
          <>
            <Button variant="outline" size="sm" onClick={addBlankRow}>
              <PlusIcon aria-hidden="true" />
              <T context="button: put a blank new row at the top of the review grid to type into">
                Add Row
              </T>
            </Button>
            <AddNewRowButton
              group={group}
              dataSourceIds={dataSourceIds}
              onAdd={addDrafts}
              onWillOpen={() => gridFocusRef.current?.blur()}
            />
            <As400GridHidingControls
              hiddenRowCount={hiddenRowCount}
              onShowRows={
                hasHiddenRows(hiddenRows)
                  ? () => onHiddenRowsChange(NO_HIDDEN_ROWS)
                  : undefined
              }
              hiddenColumnCount={hiddenHere.size}
              onShowColumns={showColumns}
            />
            <As400GridSortChip sort={sort} onClear={() => onSortChange(null)} />
          </>
        }
        newRowActions={{
          menuLabel: gt('Remove new row', {
            context: 'grid context menu: delete an authored new row',
          }),
          onDelete: (rowKey) => {
            if (isDraftRowKey(rowKey)) {
              removeDraft(rowKey);
              return;
            }
            deleteNewRow.mutate({ changeSetId, id: newRowIdOf(rowKey) });
          },
        }}
      />
    </div>
  );
}
